import React, { useEffect, useState, useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, IconButton, Tooltip, Checkbox,
  Chip, Switch, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, Snackbar, Alert, CircularProgress, Avatar,
  Divider, alpha, ListItemIcon, LinearProgress, Collapse, Fade, Slider,
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon, Refresh as RefreshIcon, MoreVert as MoreIcon,
  Settings as SettingsIcon, OpenInNew as OpenInNewIcon, EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon, Sync as SyncIcon, History as HistoryIcon,
  Person as PersonIcon, FolderOpen as FolderIcon, Check as CheckIcon, Close as CloseIcon,
  Link as LinkIcon, People as PeopleIcon, SwapVert as SwapVertIcon,
  BarChartOutlined as StatsIcon, FavoriteBorder as LikeIcon,
  ChatBubbleOutline as ReplyIcon, RepeatRounded as RepostIcon, FormatQuoteOutlined as QuoteIcon,
  PushPin as PinIcon, SignalCellularAlt as SignalIcon, Autorenew as AutorenewIcon,
  Schedule as ScheduleIcon, Whatshot as FireIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getToken } from '../api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { designTokens as t } from '../theme';

// Extrait chaque tableau JSON de cookies depuis un texte.
// Gère : plusieurs comptes collés à la suite, un par ligne, ET le format CSV
// (champ entre guillemets avec guillemets internes doublés "" -> ").
const extractJsonArrays = (text) => {
  let t = text || '';
  // Détecte un export CSV (clés entre guillemets doublés) et le décode.
  if (/""(name|domain|value|sessionid|csrftoken)""/.test(t)) {
    t = t.replace(/""/g, '"');
  }
  // Récupère chaque tableau [ ... ] (les cookies n'ont pas de [] imbriqués).
  const matches = t.match(/\[[\s\S]*?\]/g) || [];
  const out = [];
  for (let m of matches) {
    // Retire l'objet cookie "rur" : ses guillemets/backslashes échappés cassent
    // JSON.parse, et il est de toute façon supprimé (session temporaire).
    m = m.replace(/\{[^{}]*"name"\s*:\s*"rur"[^{}]*\}\s*,?/gi, '').replace(/,(\s*])/g, '$1');
    try {
      const arr = JSON.parse(m);
      if (Array.isArray(arr) && arr.some((c) => c && c.name)) out.push(m);
    } catch (e) { /* fragment non valide, ignoré */ }
  }
  return out;
};

// Format compact des abonnés : 1234 -> "1,2k"
const fmtFollowers = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1).replace('.0', '').replace('.', ',') + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1).replace('.0', '').replace('.', ',') + 'k';
  return String(v);
};

// Toggle vert propre (style iOS / shadcn) pour le statut actif/pause.
const StatusSwitch = styled(Switch)(() => ({
  width: 38,
  height: 22,
  padding: 0,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#10b981',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18,
    borderRadius: '50%',
    boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: '#3f3f46',
    opacity: 1,
    transition: 'background-color .2s',
  },
}));

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Réglages par défaut du warm-up (intensité par DURÉE, par compte).
const WARMUP_DEFAULTS = {
  durationMin: 6, durationMax: 12, like: true, likeProb: 40, maxLikes: 6, maxLikesHi: 14,
  comment: false, commentProb: 10, maxComments: 1, maxCommentsHi: 3, commentsText: '',
  view: true, viewProb: 30, minDelaySec: 5, maxDelaySec: 15, concurrency: 3,
};

// Timezones proposées pour la planification (défaut Paris).
const WARM_TZS = [
  { label: 'Paris', abbr: 'CET', tz: 'Europe/Paris' },
  { label: 'London', abbr: 'GMT', tz: 'Europe/London' },
  { label: 'New York', abbr: 'ET', tz: 'America/New_York' },
  { label: 'Chicago', abbr: 'CT', tz: 'America/Chicago' },
  { label: 'Los Angeles', abbr: 'PT', tz: 'America/Los_Angeles' },
  { label: 'Tokyo', abbr: 'JST', tz: 'Asia/Tokyo' },
  { label: 'Dubai', abbr: 'GST', tz: 'Asia/Dubai' },
  { label: 'Sydney', abbr: 'AEST', tz: 'Australia/Sydney' },
];
const WARM_MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WARM_WD = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const wDateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const tzNow = (tz) => { try { return new Intl.DateTimeFormat('fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date()); } catch (e) { return ''; } };
// Heure murale (année, mois0, jour, hh, mm) dans une timezone → epoch secondes.
function zonedToUnix(y, mo, d, hh, mm, tz) {
  const utcGuess = Date.UTC(y, mo, d, hh, mm, 0);
  try {
    const dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const p = Object.fromEntries(dtf.formatToParts(new Date(utcGuess)).map((x) => [x.type, x.value]));
    const asUTC = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
    return Math.floor((utcGuess - (asUTC - utcGuess)) / 1000);
  } catch (e) { return Math.floor(utcGuess / 1000); }
}
// Formate un epoch (s) en date+heure lisible dans une timezone.
const fmtRunAt = (sec, tz) => { try { return new Intl.DateTimeFormat('fr-FR', { timeZone: tz, weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(sec * 1000)); } catch (e) { return ''; } };

// Slider de plage étirable (double poignée) — remplace les champs min/max.
function RangeSlider({ label, value, onChange, min, max, unit = '', color = '#f97316' }) {
  const [lo, hi] = value;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.72rem', color: t.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color }}>{lo} – {hi}{unit}</Typography>
      </Box>
      <Slider size="small" value={[lo, hi]} min={min} max={max} onChange={(_, v) => onChange(v)}
        valueLabelDisplay="auto" disableSwap sx={{ color, mx: 0.5, width: 'calc(100% - 8px)' }} />
    </Box>
  );
}

// Slider simple (une valeur, ex : un pourcentage).
function PctSlider({ label, value, onChange, min = 1, max = 100, unit = '%', color = '#f97316' }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.72rem', color: t.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color }}>{value}{unit}</Typography>
      </Box>
      <Slider size="small" value={Number(value) || 0} min={min} max={max} onChange={(_, v) => onChange(v)}
        valueLabelDisplay="auto" sx={{ color, mx: 0.5, width: 'calc(100% - 8px)' }} />
    </Box>
  );
}

const WARM_STATUS = {
  pending: { label: 'en attente', color: '#71717a' },
  running: { label: 'en cours', color: '#fdba74' },
  done: { label: 'terminé', color: '#34d399' },
  failed: { label: 'échec', color: '#f87171' },
  stopped: { label: 'arrêté', color: '#a1a1aa' },
};

// Parse les lignes « username  password  totp… » (le secret TOTP peut contenir
// des espaces façon Google Authenticator → tout ce qui suit le 2e champ = totp).
const parseAutoLines = (text) => {
  const rows = [];
  for (const raw of (text || '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/\s+/);
    if (parts.length < 2) continue; // au minimum username + password
    rows.push({ username: parts[0], password: parts[1], totp: parts.slice(2).join(' ') });
  }
  return rows;
};

// ── Store « Login auto » au niveau MODULE ────────────────────────────────────
// La boucle de login tourne CÔTÉ SERVEUR (job en arrière-plan, voir
// autologin_jobs.go) : on peut quitter la page, fermer l'onglet ou éteindre le
// PC, le serveur continue. Ce store ne fait que (1) garder le texte saisi tant
// qu'il n'est pas envoyé et (2) suivre la progression en interrogeant
// /api/sessions/auto-login/status pendant qu'un job tourne.
const autoLoginStore = (() => {
  let state = { running: false, items: [], group: '', text: '', loaded: false };
  const listeners = new Set();
  const emit = () => listeners.forEach((l) => l());
  const getSnapshot = () => state;
  const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
  const set = (patch) => {
    state = typeof patch === 'function' ? patch(state) : { ...state, ...patch };
    emit();
  };

  let pollTimer = null;
  let onFinish = null;
  const stopPoll = () => { if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; } };
  const schedulePoll = () => {
    if (pollTimer) return;
    pollTimer = setTimeout(() => { pollTimer = null; refresh(); }, 2500);
  };
  const applyJob = (job) => {
    const wasRunning = state.running;
    if (!job) { set({ running: false, items: [], loaded: true }); return; }
    set((st) => ({ ...st, running: !!job.running, items: job.items || [], group: st.group || job.group || '', loaded: true }));
    if (job.running) schedulePoll();
    else { stopPoll(); if (wasRunning && onFinish) { const f = onFinish; onFinish = null; f(); } }
  };
  // refresh : relit l'état du job côté serveur (appelé au montage + en polling).
  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sessions/auto-login/status`);
      applyJob(data.job);
    } catch {
      if (state.running) schedulePoll(); // réseau momentanément KO : on réessaie
    }
  };

  const run = async ({ notify, fetchSessions }) => {
    if (state.running) return;
    const rows = parseAutoLines(state.text);
    if (!rows.length) { notify('Aucune ligne valide (format : identifiant  mot_de_passe  secret_totp)', 'error'); return; }
    try {
      const { data } = await axios.post(`${API}/api/sessions/auto-login/start`, { lines: rows, group: state.group });
      if (!data.ok) { notify(data.error || 'Impossible de lancer le login auto', 'error'); return; }
      onFinish = () => {
        const ok = state.items.filter((x) => x.status === 'ok').length;
        const fail = state.items.filter((x) => x.status === 'fail').length;
        const cancelled = state.items.some((x) => x.status === 'cancelled');
        const tail = fail ? `, ${fail} échec(s)` : '';
        notify(cancelled ? `Annulé — ${ok} compte(s) connecté(s)${tail}` : `${ok} compte(s) connecté(s)${tail}`, cancelled || fail ? 'warning' : 'success');
        if (fetchSessions) fetchSessions();
      };
      applyJob(data.job);
      notify('Login auto lancé côté serveur : tu peux quitter la page, ça continue en arrière-plan.', 'info');
    } catch (e) {
      notify(e.response?.data?.error || e.message, 'error');
    }
  };

  const cancel = async () => {
    try { await axios.post(`${API}/api/sessions/auto-login/cancel`); } catch { /* ignore */ }
    refresh();
  };
  const clear = async () => {
    if (state.running) return;
    try { await axios.post(`${API}/api/sessions/auto-login/clear`); } catch { /* ignore */ }
    set({ items: [] });
  };
  const isRunning = () => state.running;
  const hasProgress = () => state.items.length > 0;

  return { getSnapshot, subscribe, set, run, cancel, isRunning, hasProgress, clear, refresh };
})();

// Palette de couleurs assignées aux nouveaux groupes.
const GROUP_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'];

const UA_PRESETS = [
  ['Samsung Galaxy S21 Ultra', 'Instagram 269.0.0.18.75 Android (13/13; 480dpi; 1080x2400; SM-G998B; Galaxy S21 Ultra; TP1A.220624.014; qcom; en_US; 422559548)'],
  ['Samsung Galaxy S22 Ultra', 'Instagram 269.0.0.18.75 Android (12/12; 480dpi; 1080x2340; SM-S908B; Galaxy S22 Ultra; SP1A.210812.016; qcom; en_US; 422559548)'],
  ['Samsung Galaxy S23', 'Instagram 269.0.0.18.75 Android (14/14; 480dpi; 1080x2400; SM-S918B; Galaxy S23; TQ2A.230505.002; qcom; en_US; 422559548)'],
  ['Google Pixel 6', 'Instagram 269.0.0.18.75 Android (12/12; 480dpi; 1080x2340; Pixel 6; Pixel 6; SP2A.220505.008; qcom; en_US; 422559548)'],
  ['Google Pixel 7', 'Instagram 269.0.0.18.75 Android (13/13; 480dpi; 1080x2400; Pixel 7; Pixel 7; TQ1A.221205.011; qcom; en_US; 422559548)'],
  ['Google Pixel 8', 'Instagram 269.0.0.18.75 Android (14/14; 480dpi; 1080x2400; Pixel 8; Pixel 8; UQ1A.240205.004; qcom; en_US; 422559548)'],
  ['OnePlus 9 Pro', 'Instagram 269.0.0.18.75 Android (13/13; 480dpi; 1080x2400; OnePlus9Pro; OnePlus 9 Pro; RKQ1.211119.001; qcom; en_US; 422559548)'],
  ['Xiaomi Mi 11', 'Instagram 269.0.0.18.75 Android (12/12; 480dpi; 1080x2400; M2102J20SG; Xiaomi Mi 11; RKQ1.200826.002; qcom; en_US; 422559548)'],
];

// ---- helpers -----------------------------------------------------------
const metaOf = (s) => (s && s.Data && s.Data.meta) || {};
// Santé du compte (drapeaux posés par le serveur, voir account_health.go) :
//   suspended       → suspension CONFIRMÉE (profil public introuvable)
//   session_expired → cookies morts, compte vivant → « Reconnecter »
//   challenge       → vérification Instagram demandée
const isExpired = (s) => metaOf(s).session_expired === true && metaOf(s).suspended !== true;
const isChallenge = (s) => metaOf(s).challenge === true && metaOf(s).suspended !== true;
const needsAttention = (s) => metaOf(s).suspended === true || isExpired(s) || isChallenge(s);

function personaSummary(meta) {
  const bits = [];
  if (meta.p_age) bits.push(`${meta.p_age} ans`);
  if (meta.p_location) bits.push(meta.p_location);
  if (!bits.length && meta.p_occupation) bits.push(meta.p_occupation);
  return bits.join(' · ');
}

// "in 6m" / "in 3h05" / "in 2j" à partir d'un horaire de post programmé
// (timestamp ms ou chaîne ISO). Renvoie null si rien / déjà passé.
function relativeNext(value) {
  if (!value) return null;
  const ts = typeof value === 'number' ? value : new Date(value).getTime();
  if (Number.isNaN(ts)) return null;
  const diff = (ts - Date.now()) / 1000;
  if (diff <= 30) return null; // sur le point de partir
  if (diff < 3600) return `in ${Math.round(diff / 60)}m`;
  if (diff < 86400) return `in ${Math.floor(diff / 3600)}h${String(Math.round((diff % 3600) / 60)).padStart(2, '0')}`;
  return `in ${Math.floor(diff / 86400)}j`;
}

const slug = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `g_${Date.now()}`;

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState({});           // { name: {name,color,isActive} }
  const [nextPosts, setNextPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkFilter, setLinkFilter] = useState('all'); // all | with | without
  const [groupFilter, setGroupFilter] = useState(''); // '' = tous, sinon nom du groupe
  const [sortFollowers, setSortFollowers] = useState('none'); // none | desc | asc
  const [sortNext, setSortNext] = useState('none'); // none | asc (plus proche d'abord) | desc (plus lointain d'abord)
  const [selection, setSelection] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // menus
  const [groupMenu, setGroupMenu] = useState({ anchor: null, session: null });
  const [actionMenu, setActionMenu] = useState({ anchor: null, session: null });
  const [bulkGroupAnchor, setBulkGroupAnchor] = useState(null); // menu "Déplacer" (sélection)

  // édition de profil Threads (bio / nom / lien)
  const [profileDlg, setProfileDlg] = useState({ open: false, session: null, name: '', biography: '', link_url: '', link_title: '', saving: false, loading: false });

  // notes inline edit
  const [editingNotes, setEditingNotes] = useState(null); // session name
  const [notesDraft, setNotesDraft] = useState('');

  // Login auto : comptes reconnectables (creds stockés) + compte en cours de reconnexion
  const [reloginable, setReloginable] = useState(new Set());
  const [reloginBusy, setReloginBusy] = useState(null); // nom du compte en cours

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState('simple'); // simple | bulk | autologin
  const [bulk, setBulk] = useState({ text: '', files: [], group: '', importing: false, done: 0, total: 0, ok: [], fail: [] });
  // Login auto : la progression vit dans un store module (survit à la navigation
  // + snapshot localStorage pour survivre à un refresh). On s'y abonne ici.
  const auto = useSyncExternalStore(autoLoginStore.subscribe, autoLoginStore.getSnapshot);
  // Au montage : relire l'état du job côté serveur ; s'il tourne, rouvrir
  // l'onglet « Login auto » pour voir la progression live.
  useEffect(() => {
    autoLoginStore.refresh().then(() => {
      if (autoLoginStore.isRunning()) { setCreateMode('autologin'); setCreateOpen(true); }
    });
  }, []);
  const [editOpen, setEditOpen] = useState(false);
  // lien de rotation 4G du proxy du compte en cours d'édition
  const [editRotate, setEditRotate] = useState('');
  const [editRotateOrig, setEditRotateOrig] = useState('');
  const [editRotateTesting, setEditRotateTesting] = useState(false);
  const [editRotateResult, setEditRotateResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [refreshing, setRefreshing] = useState({ active: false, done: 0, total: 0 });
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsData, setStatsData] = useState({ session: null, posts: [], loading: false });
  const [historyData, setHistoryData] = useState({ session: null, history: [], loading: false });

  // ---- warm-up ---------------------------------------------------------
  const [warmupOpen, setWarmupOpen] = useState(false);
  const [warmupSel, setWarmupSel] = useState(new Set());   // comptes cochés dans la modale
  const [warmupGroupFilter, setWarmupGroupFilter] = useState('all');
  const [warmupSearch, setWarmupSearch] = useState('');
  const [warmupCfg, setWarmupCfg] = useState(() => {
    try { return { ...WARMUP_DEFAULTS, ...JSON.parse(localStorage.getItem('gt.warmup.cfg') || '{}') }; }
    catch (e) { return WARMUP_DEFAULTS; }
  });
  const [warmupRun, setWarmupRun] = useState({ running: false, accounts: [] });
  const [warmupStarting, setWarmupStarting] = useState(false);
  const [warmupExpanded, setWarmupExpanded] = useState(new Set()); // comptes dépliés (détail)
  // planification (calendrier + timezone)
  const [warmupDates, setWarmupDates] = useState(new Set()); // 'YYYY-MM-DD'
  const [warmupTime, setWarmupTime] = useState('10:00');
  const [warmupTz, setWarmupTz] = useState('Europe/Paris');
  const [warmupMonth, setWarmupMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [warmupSchedules, setWarmupSchedules] = useState([]);
  const setCfg = (k, v) => setWarmupCfg((p) => ({ ...p, [k]: v }));
  const toggleWarmExpand = (name) => setWarmupExpanded((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleWarmDate = (d) => setWarmupDates((prev) => { const k = wDateKey(d); const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // En revenant sur la page pendant qu'un warm-up tourne → rouvrir la modale
  // pour revoir la progression live (le run continue côté serveur).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/sessions/warmup`);
        if (alive && data && data.running) { setWarmupRun(data); setWarmupOpen(true); }
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  // Poll de la progression + liste des warm-ups programmés tant que le dialog est ouvert.
  useEffect(() => {
    if (!warmupOpen) return undefined;
    let alive = true;
    const tick = async () => {
      try { const { data } = await axios.get(`${API}/api/sessions/warmup`); if (alive) setWarmupRun(data || { running: false, accounts: [] }); }
      catch (e) { /* ignore */ }
    };
    const tickSched = async () => {
      try { const { data } = await axios.get(`${API}/api/sessions/warmup/schedules`); if (alive) setWarmupSchedules(data.schedules || []); }
      catch (e) { /* ignore */ }
    };
    tick(); tickSched();
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [warmupOpen]);

  const openWarmup = (names) => {
    setActionMenu({ anchor: null, session: null });
    const init = (names && names.length ? names : [...selection]);
    setWarmupSel(new Set(init));
    setWarmupGroupFilter('all');
    setWarmupSearch('');
    setWarmupDates(new Set());
    setWarmupOpen(true);
  };

  // Comptes visibles dans le picker (filtre groupe + recherche), façon Smart Post.
  const warmupVisible = useMemo(() => {
    let list = sessions;
    if (warmupGroupFilter !== 'all') list = list.filter((s) => (s.Profile || metaOf(s).profile || '') === warmupGroupFilter);
    const q = warmupSearch.trim().toLowerCase();
    if (q) list = list.filter((s) => { const meta = metaOf(s); return (meta.username || s.Name || '').toLowerCase().includes(q); });
    return list;
  }, [sessions, warmupGroupFilter, warmupSearch]);
  const warmupAllVisibleSel = warmupVisible.length > 0 && warmupVisible.every((s) => warmupSel.has(s.Name));
  const warmupToggleAll = () => setWarmupSel((prev) => {
    const n = new Set(prev);
    if (warmupAllVisibleSel) warmupVisible.forEach((s) => n.delete(s.Name));
    else warmupVisible.forEach((s) => n.add(s.Name));
    return n;
  });
  const warmupToggleOne = (name) => setWarmupSel((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const warmupCount = warmupSel.size;

  // Jours du mois affiché dans le calendrier (lundi en tête).
  const warmupCalDays = useMemo(() => {
    const y = warmupMonth.getFullYear(); const m = warmupMonth.getMonth();
    const startWd = (new Date(y, m, 1).getDay() + 6) % 7;
    const nDays = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWd; i += 1) arr.push(null);
    for (let d = 1; d <= nDays; d += 1) arr.push(new Date(y, m, d));
    return arr;
  }, [warmupMonth]);

  // Construit l'objet options (null si commentaires manquants).
  const buildWarmupOptions = () => {
    const comments = (warmupCfg.commentsText || '').split('\n').map((s) => s.trim()).filter(Boolean);
    if (warmupCfg.comment && comments.length === 0) { notify('Ajoute au moins un commentaire, ou désactive « Commenter ».', 'warning'); return null; }
    return {
      durationMin: Number(warmupCfg.durationMin) || 5, durationMax: Number(warmupCfg.durationMax) || 0,
      like: !!warmupCfg.like, likeProb: Number(warmupCfg.likeProb) || 0,
      maxLikes: Number(warmupCfg.maxLikes) || 0, maxLikesHi: Number(warmupCfg.maxLikesHi) || 0,
      comment: !!warmupCfg.comment, commentProb: Number(warmupCfg.commentProb) || 0,
      maxComments: Number(warmupCfg.maxComments) || 0, maxCommentsHi: Number(warmupCfg.maxCommentsHi) || 0, comments,
      view: !!warmupCfg.view, viewProb: Number(warmupCfg.viewProb) || 0,
      minDelaySec: Number(warmupCfg.minDelaySec) || 5, maxDelaySec: Number(warmupCfg.maxDelaySec) || 15,
      concurrency: Number(warmupCfg.concurrency) || 3,
    };
  };

  const startWarmupRun = async () => {
    if (warmupSel.size === 0) { notify('Sélectionne au moins un compte.', 'warning'); return; }
    const options = buildWarmupOptions();
    if (!options) return;
    setWarmupStarting(true);
    try {
      const { data } = await axios.post(`${API}/api/sessions/warmup`, { accounts: [...warmupSel], group: '', options });
      localStorage.setItem('gt.warmup.cfg', JSON.stringify(warmupCfg));
      notify(`🔥 Warm-up lancé sur ${data.count} compte(s).`, 'success');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally { setWarmupStarting(false); }
  };

  const scheduleWarmupRun = async () => {
    if (warmupSel.size === 0) { notify('Sélectionne au moins un compte.', 'warning'); return; }
    if (warmupDates.size === 0) { notify('Choisis au moins une date.', 'warning'); return; }
    const options = buildWarmupOptions();
    if (!options) return;
    const [hh, mm] = (warmupTime || '10:00').split(':').map((x) => parseInt(x, 10) || 0);
    const runAts = [...warmupDates].map((k) => { const [y, mo, d] = k.split('-').map(Number); return zonedToUnix(y, mo - 1, d, hh, mm, warmupTz); });
    setWarmupStarting(true);
    try {
      const { data } = await axios.post(`${API}/api/sessions/warmup/schedule`, { accounts: [...warmupSel], options, runAts });
      localStorage.setItem('gt.warmup.cfg', JSON.stringify(warmupCfg));
      notify(`🗓️ ${data.count} warm-up(s) programmé(s).`, 'success');
      setWarmupDates(new Set());
      try { const r = await axios.get(`${API}/api/sessions/warmup/schedules`); setWarmupSchedules(r.data.schedules || []); } catch (e) { /* ignore */ }
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally { setWarmupStarting(false); }
  };

  const cancelWarmupSchedule = async (id) => {
    try { await axios.post(`${API}/api/sessions/warmup/unschedule`, { id }); setWarmupSchedules((p) => p.filter((s) => s.id !== id)); }
    catch (e) { notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error'); }
  };

  const stopWarmupRun = async () => {
    try { await axios.post(`${API}/api/sessions/warmup/stop`); notify('Arrêt du warm-up demandé…', 'info'); }
    catch (e) { notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error'); }
  };

  const handleRepublishLast = async (s) => {
    setActionMenu({ anchor: null, session: null });
    if (!s) return;
    try {
      await axios.post(`${API}/api/sessions/${encodeURIComponent(s.Name)}/republish-last`);
      notify('♻️ Dernier post reprogrammé — publication dans ~1 min.', 'success');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const [selectedSession, setSelectedSession] = useState(null);
  const [editForm, setEditForm] = useState(blankEdit());
  const [createForm, setCreateForm] = useState(blankCreate());
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [testingCookies, setTestingCookies] = useState(false);
  const [rurRemoved, setRurRemoved] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: '', color: GROUP_COLORS[0] });

  function blankEdit() {
    return { name: '', proxy_url: '', user_agent: '', cooldown_min: 90, profile: '', username: '',
      full_name: '', p_age: '', p_origin: '', p_location: '', p_occupation: '' };
  }
  function blankCreate() {
    return { name: '', profile: '', cookies: null, cookiesFileName: '', username: '' };
  }

  // ---- data fetching ---------------------------------------------------
  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/profiles/config`);
      setGroups(data?.profiles || {});
    } catch (e) { /* noop */ }
  }, []);

  // Comptes ayant des identifiants de login auto stockés → bouton « Reconnecter ».
  const fetchReloginable = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sessions/autologin-accounts`);
      setReloginable(new Set(data?.accounts || []));
    } catch (e) { /* noop */ }
  }, []);

  // "Next post" = prochain post RÉELLEMENT programmé par l'utilisateur (Create Post).
  // On ne se base plus sur le cooldown/auto-post (désactivé) : si rien n'est
  // programmé pour un compte → pas d'entrée → la colonne affiche "—".
  const fetchNextPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/scheduled-posts`);
      const posts = Array.isArray(data) ? data : (data?.posts || []);
      const now = Date.now();
      const map = {};
      posts.forEach((p) => {
        if (p.published || p.Published || p.error || p.Error) return; // déjà publié / échoué
        const sess = p.session || p.Session;
        const iso = p.scheduledTime || p.ScheduledTime;
        if (!sess || !iso) return;
        const ts = new Date(iso).getTime();
        if (Number.isNaN(ts) || ts < now - 60000) return; // ignore le passé
        if (!map[sess] || ts < map[sess]) map[sess] = ts; // garde le plus proche
      });
      setNextPosts(map);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchGroups();
    fetchNextPosts();
    fetchReloginable();
    const id = setInterval(() => { fetchSessions(); fetchNextPosts(); }, 30000);
    return () => clearInterval(id);
  }, [fetchSessions, fetchGroups, fetchNextPosts, fetchReloginable]);
  // Quand le job se termine (même lancé depuis une autre session de page) → recharger la liste.
  const autoWasRunning = useRef(false);
  useEffect(() => {
    if (autoWasRunning.current && !auto.running) { fetchSessions(); fetchReloginable(); }
    autoWasRunning.current = auto.running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto.running]);

  // groups list with resolved colors + counts
  const groupList = useMemo(() => {
    return Object.entries(groups).map(([id, g], i) => ({
      id,
      name: g.name || id,
      color: g.color || GROUP_COLORS[i % GROUP_COLORS.length],
    }));
  }, [groups]);

  const groupByName = useCallback((profile) => {
    if (!profile) return null;
    return groupList.find((g) => g.name === profile || g.id === profile) || null;
  }, [groupList]);

  const groupCounts = useMemo(() => {
    const m = {};
    sessions.forEach((s) => { const p = s.Profile || metaOf(s).profile; if (p) m[p] = (m[p] || 0) + 1; });
    return m;
  }, [sessions]);

  const isPaused = (s) => metaOf(s).status === 'paused';
  // Santé du compte (posée par le serveur, voir account_health.go) :
  //   suspended       → suspension CONFIRMÉE (profil public introuvable)
  //   session_expired → cookies morts, compte vivant → « Reconnecter »
  //   challenge       → vérification Instagram demandée
  const isSuspended = (s) => metaOf(s).suspended === true;
  const activeCount = useMemo(() => sessions.filter((s) => !isPaused(s) && !needsAttention(s)).length, [sessions]);
  const suspendedCount = useMemo(() => sessions.filter((s) => isSuspended(s)).length, [sessions]);
  const expiredCount = useMemo(() => sessions.filter((s) => isExpired(s) || isChallenge(s)).length, [sessions]);

  const filtered = useMemo(() => {
    let list = sessions.filter((s) => {
      const meta = metaOf(s);
      if (statusFilter === 'active' && (isPaused(s) || needsAttention(s))) return false;
      if (statusFilter === 'paused' && !isPaused(s)) return false;
      if (statusFilter === 'suspended' && !needsAttention(s)) return false;
      if (statusFilter === 'rate' && !meta.critical_issue && !meta.needs_action) return false;
      if (linkFilter === 'with' && !meta.has_link) return false;
      if (linkFilter === 'without' && meta.has_link) return false;
      if (groupFilter && (s.Profile || meta.profile || '') !== groupFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${s.Name} ${meta.username || ''} ${meta.full_name || ''} ${meta.notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (sortFollowers !== 'none') {
      list = [...list].sort((a, b) => {
        const fa = Number(metaOf(a).followers) || 0;
        const fb = Number(metaOf(b).followers) || 0;
        return sortFollowers === 'desc' ? fb - fa : fa - fb;
      });
    }
    if (sortNext !== 'none') {
      list = [...list].sort((a, b) => {
        const ta = nextPosts[a.Name];
        const tb = nextPosts[b.Name];
        const va = typeof ta === 'number' ? ta : null;
        const vb = typeof tb === 'number' ? tb : null;
        // les comptes sans post programmé vont toujours en bas
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return sortNext === 'asc' ? va - vb : vb - va;
      });
    }
    return list;
  }, [sessions, statusFilter, search, linkFilter, sortFollowers, sortNext, nextPosts, groupFilter]);

  // ---- quick mutations (no proxy/UA wipe) ------------------------------
  const patchMeta = async (name, payload, optimistic) => {
    if (optimistic) {
      setSessions((prev) => prev.map((s) => s.Name === name
        ? { ...s, ...optimistic.session, Data: { ...s.Data, meta: { ...metaOf(s), ...optimistic.meta } } }
        : s));
    }
    try {
      await axios.post(`${API}/api/sessions/${encodeURIComponent(name)}/persona`, payload);
    } catch (e) {
      notify('Erreur de mise à jour', 'error');
      fetchSessions();
    }
  };

  const handleToggleStatus = (s) => {
    const next = isPaused(s) ? 'active' : 'paused';
    patchMeta(s.Name, { status: next }, { meta: { status: next } });
  };

  const handleAssignGroup = (s, groupName) => {
    setGroupMenu({ anchor: null, session: null });
    patchMeta(s.Name, { profile: groupName }, { session: { Profile: groupName }, meta: { profile: groupName } });
  };

  // Déplace tous les comptes sélectionnés vers un groupe (ou "aucun" si '').
  const handleBulkAssignGroup = async (groupName) => {
    setBulkGroupAnchor(null);
    const names = [...selection];
    // maj optimiste locale
    setSessions((prev) => prev.map((s) => (selection.has(s.Name)
      ? { ...s, Profile: groupName, Data: { ...s.Data, meta: { ...metaOf(s), profile: groupName } } }
      : s)));
    let fail = 0;
    await Promise.all(names.map((name) =>
      axios.post(`${API}/api/sessions/${encodeURIComponent(name)}/persona`, { profile: groupName })
        .catch(() => { fail += 1; })));
    if (fail > 0) { notify(`${fail} compte(s) non déplacé(s)`, 'error'); fetchSessions(); }
    else {
      const label = groupName ? `« ${groupByName(groupName)?.name || groupName} »` : 'aucun groupe';
      notify(`${names.length} compte(s) déplacé(s) vers ${label}`);
    }
  };

  const saveNotes = (s) => {
    const val = notesDraft;
    setEditingNotes(null);
    patchMeta(s.Name, { notes: val }, { meta: { notes: val } });
  };

  // ---- selection -------------------------------------------------------
  const allSelected = filtered.length > 0 && filtered.every((s) => selection.has(s.Name));
  const toggleAll = () => setSelection(allSelected ? new Set() : new Set(filtered.map((s) => s.Name)));
  const toggleOne = (name) => setSelection((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });

  // ---- create group ----------------------------------------------------
  const handleAddGroup = async () => {
    const name = newGroup.name.trim();
    if (!name) { notify('Nom de groupe requis', 'error'); return; }
    const id = slug(name);
    try {
      await axios.post(`${API}/api/profiles/config/${encodeURIComponent(id)}`, {
        name,
        color: newGroup.color,
        maxPostsPerDay: 20,
        allowedHours: { start: 8, end: 22 },
        isActive: true,
      });
      setAddGroupOpen(false);
      setNewGroup({ name: '', color: GROUP_COLORS[(groupList.length + 1) % GROUP_COLORS.length] });
      fetchGroups();
      notify(`Groupe « ${name} » créé`);
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur création groupe', 'error');
    }
  };

  // ---- edit ------------------------------------------------------------
  const openEdit = (s) => {
    const meta = metaOf(s);
    setSelectedSession(s);
    setEditForm({
      name: s.Name,
      proxy_url: s.ProxyURL || '',
      user_agent: s.UserAgent || '',
      cooldown_min: s.CooldownMin || 90,
      profile: s.Profile || meta.profile || '',
      username: meta.username || s.Name,
      full_name: meta.full_name || '',
      p_age: meta.p_age != null ? String(meta.p_age) : '',
      p_origin: meta.p_origin || '',
      p_location: meta.p_location || '',
      p_occupation: meta.p_occupation || '',
    });
    // Charger le lien de rotation 4G associé au proxy du compte (s'il y en a un).
    setEditRotate(''); setEditRotateOrig(''); setEditRotateResult(null);
    const px = s.ProxyURL || '';
    if (px && px.toLowerCase() !== 'direct') {
      axios.get(`${API}/api/proxies/rotate-link`, { params: { proxy: px } })
        .then(({ data }) => { setEditRotate(data.rotateUrl || ''); setEditRotateOrig(data.rotateUrl || ''); })
        .catch(() => {});
    }
    setEditOpen(true);
  };

  const testEditRotate = async () => {
    if (!editRotate.trim()) return;
    setEditRotateTesting(true); setEditRotateResult(null);
    try {
      const { data } = await axios.post(`${API}/api/proxies/rotate-test`, { rotateUrl: editRotate.trim() });
      setEditRotateResult(data);
    } catch (e) {
      setEditRotateResult({ success: false, error: e.response?.data?.error || e.message });
    } finally { setEditRotateTesting(false); }
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${API}/api/sessions/${encodeURIComponent(selectedSession.Name)}`, {
        name: editForm.name,
        proxy_url: editForm.proxy_url,
        user_agent: editForm.user_agent,
        cooldown_min: editForm.cooldown_min,
        profile: editForm.profile,
        username: editForm.username,
        preserve_created_at: true,
      });
      await axios.post(`${API}/api/sessions/${encodeURIComponent(selectedSession.Name)}/persona`, {
        full_name: editForm.full_name, p_age: editForm.p_age, p_origin: editForm.p_origin,
        p_location: editForm.p_location, p_occupation: editForm.p_occupation,
      });
      // Persister le lien de rotation 4G s'il a changé (proxy réel requis).
      const px = (editForm.proxy_url || '').trim();
      if (px && px.toLowerCase() !== 'direct' && editRotate.trim() !== editRotateOrig.trim()) {
        try { await axios.post(`${API}/api/proxies/rotate-link`, { proxy: px, rotateUrl: editRotate.trim() }); }
        catch (e) { /* non bloquant */ }
      }
      setEditOpen(false);
      fetchSessions();
      notify('Compte mis à jour');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  // ---- create (cookies) ------------------------------------------------
  const handleCookieFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTestResult(null);
    setCreateForm((p) => ({ ...p, cookiesFileName: file.name }));
    const reader = new FileReader();
    reader.onload = (ev) => setCookiesValue(ev.target.result);
    reader.readAsText(file);
  };

  // Retire le cookie "rur" (rend la session temporaire / s'auto-détruit).
  // Ne reformate que si le JSON est valide et contient un rur, pour ne pas gêner la saisie.
  const sanitizeCookies = (raw) => {
    if (typeof raw !== 'string' || !raw.trim()) return { value: raw, removed: false };
    try {
      const parsed = JSON.parse(raw);
      const isArr = Array.isArray(parsed);
      const list = isArr ? parsed : Array.isArray(parsed.cookies) ? parsed.cookies : null;
      if (!list) return { value: raw, removed: false };
      const cleaned = list.filter((c) => !(c && typeof c.name === 'string' && c.name.toLowerCase() === 'rur'));
      if (cleaned.length === list.length) return { value: raw, removed: false };
      const next = isArr ? cleaned : { ...parsed, cookies: cleaned };
      return { value: JSON.stringify(next, null, 2), removed: true };
    } catch (e) {
      return { value: raw, removed: false };
    }
  };

  const setCookiesValue = (raw) => {
    const { value, removed } = sanitizeCookies(raw);
    setTestResult(null);
    setRurRemoved(removed);
    setCreateForm((p) => ({ ...p, cookies: value }));
  };

  const analyzeCookies = (raw) => {
    if (!raw || !raw.trim()) return { state: 'empty' };
    try {
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : Array.isArray(parsed.cookies) ? parsed.cookies : null;
      if (!list) return { state: 'invalid', message: 'Format de cookies non reconnu' };
      const names = list.map((c) => c && c.name).filter(Boolean);
      const ds = list.find((c) => c && c.name === 'ds_user_id');
      return { state: 'valid', count: list.length, hasSession: names.includes('sessionid'), hasCsrf: names.includes('csrftoken'), dsUserId: ds ? ds.value : null };
    } catch (e) {
      return { state: 'invalid', message: 'JSON invalide' };
    }
  };

  const handleTestCookies = async () => {
    if (!createForm.cookies) return;
    setTestingCookies(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/sessions/test-cookies`, { cookies: createForm.cookies });
      setTestResult(data);
      if (data.valid && data.username) {
        setCreateForm((p) => ({ ...p, username: data.username, name: p.name?.trim() ? p.name : data.username }));
      }
    } catch (e) {
      setTestResult({ valid: false, error: e.response?.data?.error || e.message });
    } finally {
      setTestingCookies(false);
    }
  };

  const handleBulkFiles = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    const contents = await Promise.all(files.map((f) => f.text()));
    setBulk((p) => ({ ...p, files: [...p.files, ...contents] }));
    e.target.value = '';
  };

  const handleBulkImport = async () => {
    const blobs = [...bulk.files, ...extractJsonArrays(bulk.text)];
    if (!blobs.length) { notify('Aucun compte détecté (fichiers ou JSON collés)', 'error'); return; }
    setBulk((p) => ({ ...p, importing: true, done: 0, total: blobs.length, ok: [], fail: [] }));
    const ok = [], fail = [];
    for (let i = 0; i < blobs.length; i++) {
      const cookies = sanitizeCookies(blobs[i]).value; // retire le cookie rur
      try {
        const { data: test } = await axios.post(`${API}/api/sessions/test-cookies`, { cookies });
        if (!test.valid || !test.username) {
          fail.push(test.error ? `#${i + 1}: ${test.error}` : `compte #${i + 1}`);
        } else {
          await axios.post(`${API}/api/sessions`, {
            name: test.username, profile: bulk.group, cookies,
            username: test.username, followers: test.follower_count || 0, full_name: test.full_name || '',
            profile_pic_url: test.profile_pic_url || '',
          });
          ok.push(test.username);
        }
      } catch (err) {
        fail.push(`#${i + 1}: ${err.response?.data?.error || err.message}`);
      }
      setBulk((p) => ({ ...p, done: i + 1, ok: [...ok], fail: [...fail] }));
    }
    setBulk((p) => ({ ...p, importing: false }));
    notify(`${ok.length} compte(s) créé(s)${fail.length ? `, ${fail.length} échec(s)` : ''}`, fail.length ? 'warning' : 'success');
    fetchSessions();
  };

  // Login auto : la logique (boucle + progression) est dans autoLoginStore.
  const setAuto = (patch) => autoLoginStore.set(patch);
  const cancelAutoLogin = () => autoLoginStore.cancel();
  const handleAutoLogin = () => autoLoginStore.run({ notify, fetchSessions: () => { fetchSessions(); fetchReloginable(); } });

  const handleCreateSubmit = async () => {
    if (!createForm.cookies) { notify('Collez ou importez les cookies', 'error'); return; }
    setCreateLoading(true);
    try {
      let info = testResult && testResult.valid && testResult.username ? testResult : null;
      if (!info) {
        const { data } = await axios.post(`${API}/api/sessions/test-cookies`, { cookies: createForm.cookies });
        setTestResult(data);
        if (!data.valid || !data.username) { notify(data.error || 'Session invalide', 'error'); setCreateLoading(false); return; }
        info = data;
      }
      const username = info.username;
      const sessionName = (createForm.name && createForm.name.trim()) || username;
      await axios.post(`${API}/api/sessions`, {
        name: sessionName, profile: createForm.profile, cookies: createForm.cookies,
        username, followers: info.follower_count || 0, full_name: info.full_name || '',
        profile_pic_url: info.profile_pic_url || '',
      });
      setCreateOpen(false);
      setCreateForm(blankCreate());
      setTestResult(null);
      setRurRemoved(false);
      fetchSessions();
      notify(`Compte @${username} créé`);
    } catch (e) {
      notify(`Erreur création: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ---- actions ---------------------------------------------------------
  const handleRefreshFollowers = async (s) => {
    setActionMenu({ anchor: null, session: null });
    try {
      const { data } = await axios.post(`${API}/api/sessions/${encodeURIComponent(s.Name)}/update-followers`);
      notify(data.message || 'Followers mis à jour');
      fetchSessions();
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  // Reconnecte un compte via ses identifiants de login auto stockés (cookies frais).
  const handleRelogin = async (s) => {
    setActionMenu({ anchor: null, session: null });
    if (!s) return;
    const name = s.Name;
    setReloginBusy(name);
    notify(`Reconnexion de @${metaOf(s).username || name}… (rotation IP + login, ~30s)`, 'info');
    try {
      const { data } = await axios.post(`${API}/api/sessions/${encodeURIComponent(name)}/relogin`);
      if (data.ok) {
        notify(`✅ @${data.username || name} reconnecté`, 'success');
        fetchSessions();
      } else {
        notify(`Échec reconnexion: ${data.error || 'inconnu'}`, 'error');
      }
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setReloginBusy(null);
    }
  };

  const openProfileEdit = async (s) => {
    const meta = (s && s.Data && s.Data.meta) || {};
    // ouvre tout de suite avec les valeurs locales, puis charge le profil LIVE
    setProfileDlg({
      open: true, session: s, name: meta.full_name || '', biography: meta.biography || '',
      link_url: '', link_title: '', saving: false, loading: true,
    });
    try {
      const { data } = await axios.get(`${API}/api/sessions/${encodeURIComponent(s.Name)}/profile`);
      if (data.error) {
        setProfileDlg((p) => ({ ...p, loading: false }));
        notify(`Profil live indisponible: ${data.error}`, 'warning');
        return;
      }
      const link = (data.bio_links && data.bio_links[0]) || null;
      setProfileDlg((p) => ({
        ...p,
        name: data.full_name || p.name,
        biography: data.biography || '',
        link_url: (link && (link.url || link.lynx_url || link.link_url)) || data.external_url || '',
        link_title: (link && link.title) || '',
        loading: false,
      }));
    } catch (e) {
      setProfileDlg((p) => ({ ...p, loading: false }));
      notify(`Profil live indisponible (valeurs locales affichées): ${e.response?.data?.error || e.message}`, 'warning');
    }
  };

  const handleSaveProfile = async () => {
    const s = profileDlg.session;
    if (!s) return;
    setProfileDlg((p) => ({ ...p, saving: true }));
    try {
      const { data } = await axios.post(`${API}/api/sessions/${encodeURIComponent(s.Name)}/edit-profile`, {
        name: profileDlg.name,
        biography: profileDlg.biography,
        link_url: profileDlg.link_url,
        link_title: profileDlg.link_title,
      });
      if (data.success === false || (data.error && !data.results)) {
        notify(`Échec: ${data.error || 'erreur inconnue'}`, 'error');
        setProfileDlg((p) => ({ ...p, saving: false }));
        return;
      }
      const linkRes = data.results && data.results.link;
      if (linkRes && String(linkRes).startsWith('erreur')) {
        notify(`Profil OK, mais lien : ${linkRes}`, 'warning');
      } else {
        notify('Profil Threads mis à jour ✓');
      }
      setProfileDlg({ open: false, session: null, name: '', biography: '', link_url: '', link_title: '', saving: false });
      fetchSessions();
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
      setProfileDlg((p) => ({ ...p, saving: false }));
    }
  };

  const handleViewHistory = async (s) => {
    setActionMenu({ anchor: null, session: null });
    setHistoryData({ session: s, history: [], loading: true });
    setHistoryOpen(true);
    try {
      const { data } = await axios.get(`${API}/api/sessions/${encodeURIComponent(s.Name)}/followers-history`);
      setHistoryData({ session: s, history: data.history || [], loading: false });
    } catch (e) {
      setHistoryData({ session: s, history: [], loading: false });
      notify(`Erreur historique: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const handleViewStats = async (s) => {
    setActionMenu({ anchor: null, session: null });
    setStatsData({ session: s, posts: [], loading: true });
    setStatsOpen(true);
    try {
      const { data } = await axios.get(`${API}/api/sessions/${encodeURIComponent(s.Name)}/post-stats`);
      setStatsData({ session: s, posts: data.posts || [], loading: false });
    } catch (e) {
      setStatsData({ session: s, posts: [], loading: false });
      notify(`Erreur stats: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const handleDisableSticky = async (s) => {
    setActionMenu({ anchor: null, session: null });
    try {
      await axios.post(`${API}/api/sessions/${encodeURIComponent(s.Name)}/sticky/disable`);
      fetchSessions();
      notify('Mode Sticky désactivé');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const handleDisableViralBoost = async (s) => {
    setActionMenu({ anchor: null, session: null });
    try {
      await axios.post(`${API}/api/sessions/${encodeURIComponent(s.Name)}/viral-boost/disable`);
      fetchSessions();
      notify('Boost viral arrêté — les posts normaux reprennent');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/api/sessions/${encodeURIComponent(deleteTarget.Name)}`);
      setDeleteTarget(null);
      fetchSessions();
      notify('Compte supprimé');
    } catch (e) {
      notify(`Erreur suppression: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selection.size) return;
    setDeleteLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/sessions/bulk-delete`, { sessions: [...selection] });
      setBulkDeleteOpen(false);
      setSelection(new Set());
      fetchSessions();
      notify(data.message || `${selection.size} compte(s) supprimé(s)`, (data.errors && data.errors.length) ? 'warning' : 'success');
    } catch (e) {
      notify(`Erreur: ${e.response?.data?.error || e.message}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Rafraîchit, pour chaque compte sélectionné, les abonnés + le profil (bio /
  // liens) + les stats des derniers posts, en appelant les 3 endpoints existants.
  // Concurrence limitée à 3 comptes à la fois pour ne pas marteler Threads.
  const handleBulkRefresh = async () => {
    const names = [...selection];
    if (!names.length || refreshing.active) return;
    setRefreshing({ active: true, done: 0, total: names.length });
    const failed = [];
    let done = 0;

    const refreshOne = async (name) => {
      const enc = encodeURIComponent(name);
      const res = await Promise.allSettled([
        axios.post(`${API}/api/sessions/${enc}/update-followers`),
        axios.get(`${API}/api/sessions/${enc}/profile`),
        axios.get(`${API}/api/sessions/${enc}/post-stats`),
      ]);
      // On considère le compte "à jour" si au moins les abonnés ont été récupérés
      // (le profil/les stats renvoient parfois un 200 avec {error} sans lever).
      const followers = res[0];
      return followers.status === 'fulfilled' && !followers.value?.data?.error;
    };

    let idx = 0;
    const worker = async () => {
      while (idx < names.length) {
        const name = names[idx++];
        let ok = false;
        try { ok = await refreshOne(name); } catch (e) { ok = false; }
        if (!ok) failed.push(name);
        done += 1;
        setRefreshing({ active: true, done, total: names.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, names.length) }, worker));

    setRefreshing({ active: false, done: 0, total: 0 });
    fetchSessions();
    if (failed.length === 0) {
      notify(`${names.length} compte(s) mis à jour ✓`);
    } else if (failed.length < names.length) {
      notify(`${names.length - failed.length}/${names.length} à jour — échec : ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}`, 'warning');
    } else {
      notify(`Échec de la mise à jour (${failed.length} compte(s))`, 'error');
    }
  };

  const cookieInfo = analyzeCookies(createForm.cookies);
  const FILTERS = [['all', 'All'], ['active', 'Active'], ['paused', 'Paused'], ['suspended', 'À reconnecter'], ['rate', 'Rate limited']];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ---- header ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: t.muted }}>
          <PersonIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.9rem' }}>
            <b style={{ color: t.foreground }}>{sessions.length}</b> accounts · <b style={{ color: '#34d399' }}>{activeCount}</b> active{suspendedCount > 0 && <> · <b style={{ color: '#f43f5e' }}>{suspendedCount}</b> suspended</>}{expiredCount > 0 && <> · <b style={{ color: '#fb923c' }}>{expiredCount}</b> à reconnecter</>}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={() => { fetchSessions(); fetchNextPosts(); }} sx={{ border: `1px solid ${t.border}`, borderRadius: 2 }}>
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<AutorenewIcon />} onClick={() => openWarmup([...selection])}
            sx={{ borderColor: alpha('#f97316', 0.4), color: '#fdba74',
              '&:hover': { borderColor: '#f97316', bgcolor: alpha('#f97316', 0.08) } }}>
            Warm up
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateForm(blankCreate()); setTestResult(null); setCreateOpen(true); }}>
            Nouveau compte
          </Button>
        </Box>
      </Box>

      {/* ---- groups ---- */}
      <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.mutedFaint }}>
            <FolderIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Groups</Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => { setNewGroup({ name: '', color: GROUP_COLORS[groupList.length % GROUP_COLORS.length] }); setAddGroupOpen(true); }}
            sx={{ color: t.muted, textTransform: 'none' }}>
            Add
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
          {groupList.length === 0 && (
            <Typography sx={{ fontSize: '0.82rem', color: t.mutedFaint }}>Aucun groupe. Clique sur « Add » pour en créer un.</Typography>
          )}
          {groupList.map((g) => {
            const selected = groupFilter === g.name;
            return (
              <Box key={g.id} role="button"
                onClick={() => setGroupFilter(selected ? '' : g.name)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.6, borderRadius: 2, cursor: 'pointer',
                  bgcolor: selected ? alpha(g.color, 0.16) : 'transparent',
                  border: `1px solid ${selected ? alpha(g.color, 0.5) : 'transparent'}`,
                  '&:hover': { bgcolor: alpha(g.color, selected ? 0.2 : 0.08) } }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: g.color, boxShadow: `0 0 8px ${alpha(g.color, 0.6)}` }} />
                <Typography sx={{ fontSize: '0.88rem', fontWeight: selected ? 700 : 500 }}>{g.name}</Typography>
                <Chip label={groupCounts[g.name] || 0} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha('#fff', 0.06), color: t.muted }} />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ---- toolbar ---- */}
      <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search accounts…" value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              sx={{ width: 280, '& .MuiOutlinedInput-root': { bgcolor: t.background } }} />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {FILTERS.map(([key, label]) => (
                <Box key={key} role="button" onClick={() => setStatusFilter(key)}
                  sx={{ px: 1.5, py: 0.6, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.82rem',
                    bgcolor: statusFilter === key ? alpha('#fff', 0.07) : 'transparent',
                    color: statusFilter === key ? t.foreground : t.muted, fontWeight: statusFilter === key ? 600 : 500,
                    '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                  {label}
                </Box>
              ))}
            </Box>

            {/* filtre lien */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pl: 1.5, ml: 0.5, borderLeft: `1px solid ${t.border}` }}>
              {[['all', 'Tous'], ['with', 'Avec lien'], ['without', 'Sans lien']].map(([key, label]) => (
                <Box key={key} role="button" onClick={() => setLinkFilter(key)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1.25, py: 0.6, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.8rem',
                    bgcolor: linkFilter === key ? alpha('#fff', 0.07) : 'transparent',
                    color: linkFilter === key ? t.foreground : t.muted, fontWeight: linkFilter === key ? 600 : 500,
                    '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                  {key === 'with' && <LinkIcon sx={{ fontSize: 14 }} />}
                  {label}
                </Box>
              ))}
            </Box>

            {/* tri par abonnés */}
            <Box role="button" onClick={() => { setSortNext('none'); setSortFollowers((p) => (p === 'none' ? 'desc' : p === 'desc' ? 'asc' : 'none')); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1.25, py: 0.6, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.8rem',
                bgcolor: sortFollowers !== 'none' ? alpha(t.primary, 0.15) : 'transparent',
                color: sortFollowers !== 'none' ? '#c7d2fe' : t.muted, fontWeight: sortFollowers !== 'none' ? 600 : 500,
                border: `1px solid ${sortFollowers !== 'none' ? alpha(t.primary, 0.4) : 'transparent'}`,
                '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
              <PeopleIcon sx={{ fontSize: 14 }} />
              Abonnés{sortFollowers === 'desc' ? ' ↓' : sortFollowers === 'asc' ? ' ↑' : ''}
              <SwapVertIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>{filtered.length} of {sessions.length}</Typography>
        </Box>

        {/* bulk bar */}
        {selection.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: alpha('#ef4444', 0.08), borderTop: `1px solid ${alpha('#ef4444', 0.2)}` }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{selection.size} sélectionné(s)</Typography>
            <Button size="small" disabled={refreshing.active}
              startIcon={refreshing.active ? <CircularProgress size={14} sx={{ color: '#c7d2fe' }} /> : <SyncIcon sx={{ fontSize: 18 }} />}
              onClick={handleBulkRefresh} sx={{ color: '#c7d2fe' }}>
              {refreshing.active ? `Mise à jour ${refreshing.done}/${refreshing.total}` : 'Mettre à jour'}
            </Button>
            <Button size="small" disabled={refreshing.active} startIcon={<FolderIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setBulkGroupAnchor(e.currentTarget)} sx={{ color: '#c7d2fe' }}>Déplacer</Button>
            <Button size="small" startIcon={<AutorenewIcon sx={{ fontSize: 18 }} />}
              onClick={() => openWarmup([...selection])} sx={{ color: '#fdba74' }}>Warm up</Button>
            <Button size="small" color="error" disabled={refreshing.active} startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} onClick={() => setBulkDeleteOpen(true)}>Supprimer</Button>
            <Button size="small" color="inherit" sx={{ color: t.muted }} onClick={() => setSelection(new Set())}>Annuler</Button>
          </Box>
        )}

        {/* ---- table head ---- */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`,
          color: t.mutedFaint, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selection.size > 0} onChange={toggleAll} sx={{ p: 0.5, mr: 1 }} />
          <Box sx={{ flex: 1, minWidth: 180 }}>Username</Box>
          <Box sx={{ width: 130 }}>Notes</Box>
          <Box sx={{ width: 150 }}>Group</Box>
          <Box sx={{ width: 120 }}>Persona</Box>
          <Tooltip title={sortNext === 'asc' ? 'Trié : le plus proche en haut (clic → le plus lointain)' : sortNext === 'desc' ? 'Trié : le plus lointain en haut (clic → annuler)' : 'Trier par prochain post programmé'}>
            <Box role="button"
              onClick={() => { setSortFollowers('none'); setSortNext((p) => (p === 'none' ? 'asc' : p === 'asc' ? 'desc' : 'none')); }}
              sx={{ width: 90, display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', userSelect: 'none',
                color: sortNext !== 'none' ? '#c7d2fe' : 'inherit', '&:hover': { color: t.foreground } }}>
              Next post{sortNext === 'asc' ? ' ↑' : sortNext === 'desc' ? ' ↓' : ''}
              <SwapVertIcon sx={{ fontSize: 13, opacity: sortNext !== 'none' ? 1 : 0.5 }} />
            </Box>
          </Tooltip>
          <Box sx={{ width: 70, textAlign: 'center' }}>Status</Box>
          <Box sx={{ width: 80, textAlign: 'right' }}>Actions</Box>
        </Box>

        {/* ---- rows ---- */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={26} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: t.mutedFaint }}>Aucun compte.</Box>
        ) : (
          filtered.map((s) => {
            const meta = metaOf(s);
            const g = groupByName(s.Profile || meta.profile);
            const paused = isPaused(s);
            const suspended = isSuspended(s);
            const expired = isExpired(s);
            const challenge = isChallenge(s);
            const attention = suspended || expired || challenge;
            const persona = personaSummary(meta);
            const rel = relativeNext(nextPosts[s.Name]); // prochain post programmé (ms) ou undefined
            const hasPic = !!(meta.profile_pic_url || meta.profile_pic);
            // L'avatar passe par notre backend (évite le blocage hotlink du CDN IG).
            const avatar = hasPic ? `${API}/api/sessions/${encodeURIComponent(s.Name)}/avatar?token=${getToken()}` : undefined;
            const handle = meta.username || s.Name;
            const isNew = meta.created_at && (Date.now() / 1000 - Number(meta.created_at) < 86400);
            return (
              <Box key={s.Name} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: `1px solid ${alpha(t.border, 0.6)}`,
                opacity: paused ? 0.55 : 1, '&:hover': { bgcolor: alpha('#fff', 0.02) } }}>
                <Checkbox size="small" checked={selection.has(s.Name)} onChange={() => toggleOne(s.Name)} sx={{ p: 0.5, mr: 1 }} />

                {/* username */}
                <Box sx={{ flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Avatar src={avatar || undefined} sx={{ width: 34, height: 34, bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe', fontSize: '0.85rem', fontWeight: 600 }}>
                    {(meta.full_name || handle || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{meta.full_name || s.Name}</Typography>
                      {isNew && (
                        <Box component="span" sx={{ px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', bgcolor: '#f43f5e', color: '#fff', lineHeight: 1.5, flexShrink: 0 }}>NEW</Box>
                      )}
                      {suspended && (
                        <Tooltip title="Suspension confirmée : le profil public n'existe plus côté Threads.">
                          <Box component="span" sx={{ px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', bgcolor: '#b91c1c', color: '#fff', lineHeight: 1.5, flexShrink: 0 }}>SUSPENDU</Box>
                        </Tooltip>
                      )}
                      {expired && (
                        <Tooltip title={`Cookies expirés (le compte existe toujours). ${meta.last_check_error || ''}`.trim()}>
                          <Box component="span" sx={{ px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', bgcolor: '#c2410c', color: '#fff', lineHeight: 1.5, flexShrink: 0 }}>SESSION EXPIRÉE</Box>
                        </Tooltip>
                      )}
                      {challenge && (
                        <Tooltip title={`Instagram demande une vérification (challenge / checkpoint). ${meta.last_check_error || ''}`.trim()}>
                          <Box component="span" sx={{ px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', bgcolor: '#a16207', color: '#fff', lineHeight: 1.5, flexShrink: 0 }}>CHALLENGE</Box>
                        </Tooltip>
                      )}
                      {attention && reloginable.has(s.Name) && (
                        <Tooltip title="Reconnecter ce compte avec ses identifiants stockés (nouveaux cookies)">
                          <Box component="span" role="button" onClick={(e) => { e.stopPropagation(); if (reloginBusy !== s.Name) handleRelogin(s); }}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.03em',
                              cursor: reloginBusy === s.Name ? 'default' : 'pointer', bgcolor: alpha('#f97316', 0.18), color: '#fdba74', border: `1px solid ${alpha('#f97316', 0.45)}`, lineHeight: 1.5, flexShrink: 0,
                              '&:hover': { bgcolor: alpha('#f97316', 0.3) } }}>
                            {reloginBusy === s.Name
                              ? <><CircularProgress size={9} sx={{ color: '#fdba74' }} /> RECONNEXION…</>
                              : <><AutorenewIcon sx={{ fontSize: 11 }} /> RECONNECTER</>}
                          </Box>
                        </Tooltip>
                      )}
                      {meta.sticky && meta.sticky.active && (
                        <Tooltip title="Mode Sticky actif — ce compte ne publie que le post sticky. Les posts normaux sont ignorés. (désactivable via le menu ⋯)">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', bgcolor: '#ec4899', color: '#fff', lineHeight: 1.5, flexShrink: 0 }}>
                            <PinIcon sx={{ fontSize: 10 }} /> STICKY
                          </Box>
                        </Tooltip>
                      )}
                      {Array.isArray(meta.tags) && meta.tags.map((tag) => (
                        <Box key={tag} component="span" sx={{ px: 0.65, py: 0.15, borderRadius: 1, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.03em', bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', border: `1px solid ${alpha(t.primary, 0.35)}`, lineHeight: 1.5, flexShrink: 0 }}>
                          {tag}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Typography noWrap sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>@{handle}</Typography>
                      <Tooltip title="Ouvrir le profil">
                        <IconButton size="small" component="a" href={`https://www.threads.com/@${handle}`} target="_blank" rel="noopener" sx={{ p: 0.2, color: t.mutedFaint }}>
                          <OpenInNewIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* abonnés + badge lien */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <PeopleIcon sx={{ fontSize: 12, color: t.mutedFaint }} />
                        <Typography sx={{ fontSize: '0.72rem', color: t.muted, fontWeight: 600 }}>{fmtFollowers(meta.followers)}</Typography>
                      </Box>
                      {meta.has_link && (
                        <Tooltip title="Lien dans la bio">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2, px: 0.5, py: 0.1, borderRadius: 0.75,
                            bgcolor: alpha(t.success, 0.16), border: `1px solid ${alpha(t.success, 0.3)}` }}>
                            <LinkIcon sx={{ fontSize: 11, color: '#86efac' }} />
                            <CheckIcon sx={{ fontSize: 10, color: '#86efac' }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* notes */}
                <Box sx={{ width: 130, pr: 1 }}>
                  {editingNotes === s.Name ? (
                    <TextField autoFocus size="small" variant="standard" value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      onBlur={() => saveNotes(s)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveNotes(s); if (e.key === 'Escape') setEditingNotes(null); }}
                      InputProps={{ sx: { fontSize: '0.8rem' } }} sx={{ width: '100%' }} />
                  ) : (
                    <Box role="button" onClick={() => { setEditingNotes(s.Name); setNotesDraft(meta.notes || ''); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'text', minHeight: 24, color: meta.notes ? t.muted : t.mutedFaint,
                        '&:hover .editPen': { opacity: 1 } }}>
                      <Typography noWrap sx={{ fontSize: '0.8rem' }}>{meta.notes || '—'}</Typography>
                      <EditIcon className="editPen" sx={{ fontSize: 12, opacity: 0, transition: 'opacity .15s' }} />
                    </Box>
                  )}
                </Box>

                {/* group (inline) */}
                <Box sx={{ width: 150 }}>
                  <Chip
                    onClick={(e) => setGroupMenu({ anchor: e.currentTarget, session: s })}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        {g && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: g.color }} />}
                        <span>{g ? g.name : 'Sans groupe'}</span>
                      </Box>
                    }
                    size="small"
                    deleteIcon={<span style={{ fontSize: 10, color: t.mutedFaint, marginRight: 4 }}>▾</span>}
                    onDelete={(e) => setGroupMenu({ anchor: e.currentTarget, session: s })}
                    sx={{ cursor: 'pointer', bgcolor: g ? alpha(g.color, 0.12) : alpha('#fff', 0.05),
                      color: g ? g.color : t.muted, border: `1px solid ${g ? alpha(g.color, 0.3) : t.border}`,
                      fontWeight: 500, '& .MuiChip-label': { px: 1 } }} />
                </Box>

                {/* persona */}
                <Box sx={{ width: 120 }}>
                  <Box role="button" onClick={() => openEdit(s)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: persona ? t.muted : t.mutedFaint, '&:hover': { color: t.foreground } }}>
                    <PersonIcon sx={{ fontSize: 14 }} />
                    <Typography noWrap sx={{ fontSize: '0.78rem' }}>{persona || 'None'}</Typography>
                  </Box>
                </Box>

                {/* next post = prochain post programmé via Create Post (sinon "—") */}
                <Box sx={{ width: 90 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: rel ? '#fca5a5' : t.mutedFaint, fontWeight: rel ? 600 : 400 }}>{rel || '—'}</Typography>
                </Box>

                {/* status */}
                <Box sx={{ width: 70, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={paused ? 'En pause' : 'Actif'}>
                    <StatusSwitch checked={!paused} onChange={() => handleToggleStatus(s)} />
                  </Tooltip>
                </Box>

                {/* actions */}
                <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                  <Tooltip title="Éditer">
                    <IconButton size="small" onClick={() => openEdit(s)} sx={{ color: t.mutedFaint }}><SettingsIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={(e) => setActionMenu({ anchor: e.currentTarget, session: s })} sx={{ color: t.mutedFaint }}>
                    <MoreIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* ===== group assign menu ===== */}
      <Menu anchorEl={groupMenu.anchor} open={!!groupMenu.anchor} onClose={() => setGroupMenu({ anchor: null, session: null })}
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, minWidth: 190 } }}>
        <MenuItem onClick={() => handleAssignGroup(groupMenu.session, '')}>
          <Typography sx={{ color: t.muted, fontSize: '0.85rem' }}>Sans groupe</Typography>
        </MenuItem>
        <Divider sx={{ borderColor: t.border }} />
        {groupList.map((g) => (
          <MenuItem key={g.id} onClick={() => handleAssignGroup(groupMenu.session, g.name)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color }} />
              <Typography sx={{ fontSize: '0.85rem' }}>{g.name}</Typography>
            </Box>
          </MenuItem>
        ))}
        {groupList.length === 0 && <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>Crée un groupe d'abord</Typography></MenuItem>}
      </Menu>

      {/* ===== bulk move menu (sélection → groupe) ===== */}
      <Menu anchorEl={bulkGroupAnchor} open={!!bulkGroupAnchor} onClose={() => setBulkGroupAnchor(null)}
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, minWidth: 200 } }}>
        <Typography sx={{ px: 2, py: 0.75, fontSize: '0.72rem', fontWeight: 600, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Déplacer {selection.size} compte(s) vers
        </Typography>
        <MenuItem onClick={() => handleBulkAssignGroup('')}>
          <Typography sx={{ color: t.muted, fontSize: '0.85rem' }}>Sans groupe</Typography>
        </MenuItem>
        <Divider sx={{ borderColor: t.border }} />
        {groupList.map((g) => (
          <MenuItem key={g.id} onClick={() => handleBulkAssignGroup(g.name)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color }} />
              <Typography sx={{ fontSize: '0.85rem' }}>{g.name}</Typography>
            </Box>
          </MenuItem>
        ))}
        {groupList.length === 0 && <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>Crée un groupe d'abord</Typography></MenuItem>}
      </Menu>

      {/* ===== actions menu (3 dots) ===== */}
      <Menu anchorEl={actionMenu.anchor} open={!!actionMenu.anchor} onClose={() => setActionMenu({ anchor: null, session: null })}
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, minWidth: 220 } }}>
        <MenuItem onClick={() => handleRefreshFollowers(actionMenu.session)}>
          <ListItemIcon><SyncIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Rafraîchir les abonnés
        </MenuItem>
        {actionMenu.session && reloginable.has(actionMenu.session.Name) && (
          <MenuItem onClick={() => handleRelogin(actionMenu.session)} sx={{ color: '#fdba74' }}>
            <ListItemIcon><AutorenewIcon sx={{ fontSize: 18, color: '#fdba74' }} /></ListItemIcon>Reconnecter (login auto)
          </MenuItem>
        )}
        <MenuItem onClick={() => handleViewHistory(actionMenu.session)}>
          <ListItemIcon><HistoryIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Historique des abonnés
        </MenuItem>
        <MenuItem onClick={() => handleViewStats(actionMenu.session)}>
          <ListItemIcon><StatsIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Stats des posts
        </MenuItem>
        <MenuItem onClick={() => openWarmup([actionMenu.session?.Name].filter(Boolean))} sx={{ color: '#fdba74' }}>
          <ListItemIcon><AutorenewIcon sx={{ fontSize: 18, color: '#fdba74' }} /></ListItemIcon>Warm up
        </MenuItem>
        <MenuItem onClick={() => handleRepublishLast(actionMenu.session)}>
          <ListItemIcon><RepostIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Republier dernier post
        </MenuItem>
        {actionMenu.session && metaOf(actionMenu.session).sticky && metaOf(actionMenu.session).sticky.active && (
          <MenuItem onClick={() => handleDisableSticky(actionMenu.session)} sx={{ color: '#f9a8d4' }}>
            <ListItemIcon><PinIcon sx={{ fontSize: 18, color: '#ec4899' }} /></ListItemIcon>Désactiver Sticky
          </MenuItem>
        )}
        {actionMenu.session && metaOf(actionMenu.session).viral_boost && metaOf(actionMenu.session).viral_boost.active && (
          <MenuItem onClick={() => handleDisableViralBoost(actionMenu.session)} sx={{ color: '#fca5a5' }}>
            <ListItemIcon><FireIcon sx={{ fontSize: 18, color: '#ef4444' }} /></ListItemIcon>Arrêter le boost viral
          </MenuItem>
        )}
        <MenuItem onClick={() => { const s = actionMenu.session; setActionMenu({ anchor: null, session: null }); openProfileEdit(s); }}>
          <ListItemIcon><PersonIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Éditer le profil Threads
        </MenuItem>
        <MenuItem onClick={() => { const s = actionMenu.session; setActionMenu({ anchor: null, session: null }); openEdit(s); }}>
          <ListItemIcon><EditIcon sx={{ fontSize: 18, color: t.muted }} /></ListItemIcon>Éditer persona
        </MenuItem>
        <Divider sx={{ borderColor: t.border }} />
        <MenuItem onClick={() => { const s = actionMenu.session; setActionMenu({ anchor: null, session: null }); setDeleteTarget(s); }} sx={{ color: '#f87171' }}>
          <ListItemIcon><DeleteIcon sx={{ fontSize: 18, color: '#f87171' }} /></ListItemIcon>Supprimer
        </MenuItem>
      </Menu>

      {/* ===== édition de profil Threads ===== */}
      <Dialog open={profileDlg.open} onClose={() => !profileDlg.saving && setProfileDlg((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Éditer le profil Threads · {profileDlg.session?.Name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>
              Modifie directement le profil Threads du compte (bio, nom, lien).
            </Typography>
            {profileDlg.loading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CircularProgress size={12} /><Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>profil actuel…</Typography></Box>}
          </Box>
          <TextField margin="dense" label="Nom affiché" fullWidth value={profileDlg.name}
            onChange={(e) => setProfileDlg((p) => ({ ...p, name: e.target.value }))} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Bio" fullWidth multiline minRows={3} inputProps={{ maxLength: 500 }} value={profileDlg.biography}
            onChange={(e) => setProfileDlg((p) => ({ ...p, biography: e.target.value }))}
            helperText={`${profileDlg.biography.length}/500`} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField margin="dense" label="Lien (URL)" placeholder="exemple.com" fullWidth value={profileDlg.link_url}
              onChange={(e) => setProfileDlg((p) => ({ ...p, link_url: e.target.value }))} />
            <TextField margin="dense" label="Titre du lien (optionnel)" fullWidth value={profileDlg.link_title}
              onChange={(e) => setProfileDlg((p) => ({ ...p, link_title: e.target.value }))} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 1 }}>
            Laisse le lien vide pour ne pas y toucher.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDlg((p) => ({ ...p, open: false }))} disabled={profileDlg.saving}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={profileDlg.saving}>
            {profileDlg.saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Appliquer sur Threads'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== warm-up dialog ===== */}
      <Dialog open={warmupOpen} onClose={() => setWarmupOpen(false)} maxWidth="md" fullWidth
        TransitionComponent={Fade} transitionDuration={220}
        PaperProps={{ sx: {
          bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 4, overflow: 'hidden',
          '@keyframes warmFloat': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-3px)' } },
          '@keyframes warmPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
          '@keyframes warmSheen': { '0%': { backgroundPosition: '-140% 0' }, '100%': { backgroundPosition: '240% 0' } },
          '@keyframes warmIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        } }}>

        {/* ---- header banner ---- */}
        <Box sx={{ position: 'relative', px: 3, pt: 2.75, pb: 2.25, overflow: 'hidden',
          background: `radial-gradient(120% 140% at 0% 0%, ${alpha('#f97316', 0.22)} 0%, ${alpha('#f97316', 0)} 55%), ${t.card}`,
          borderBottom: `1px solid ${t.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 46, height: 46, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #fb923c 0%, #f97316 60%, #ea580c 100%)',
              boxShadow: `0 8px 22px -6px ${alpha('#f97316', 0.7)}`,
              animation: warmupRun.running ? 'warmFloat 2.4s ease-in-out infinite' : 'none' }}>
              <AutorenewIcon sx={{ color: '#fff', fontSize: 26,
                animation: warmupRun.running ? 'spin 3s linear infinite' : 'none',
                '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '1.12rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Warm up</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>
                Chauffe tes comptes comme un vrai humain — scroll, like, vue, commentaire.
              </Typography>
            </Box>
            <IconButton onClick={() => setWarmupOpen(false)} sx={{ color: t.mutedFaint }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>

            {/* ===== colonne comptes (façon Smart Post) ===== */}
            <Box sx={{ width: { xs: '100%', md: 288 }, flexShrink: 0, p: 2,
              borderRight: { md: `1px solid ${t.border}` }, borderBottom: { xs: `1px solid ${t.border}`, md: 'none' } }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1.5 }}>Comptes</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <Select value={warmupGroupFilter} onChange={(e) => setWarmupGroupFilter(e.target.value)} sx={{ bgcolor: t.background }}>
                  <MenuItem value="all">Tous les groupes</MenuItem>
                  {groupList.map((g) => (
                    <MenuItem key={g.id} value={g.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color }} />{g.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth size="small" placeholder="Rechercher…" value={warmupSearch} onChange={(e) => setWarmupSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { bgcolor: t.background } }} />
              <Button fullWidth variant="outlined" color="inherit" startIcon={<CheckIcon sx={{ fontSize: 18 }} />} onClick={warmupToggleAll}
                sx={{ borderColor: t.border, color: t.muted, mb: 1.5 }}>
                {warmupAllVisibleSel ? 'Tout désélectionner' : `Tout sélectionner (${warmupVisible.length})`}
              </Button>
              <Box sx={{ maxHeight: { xs: 220, md: 420 }, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {warmupVisible.map((s) => {
                  const meta = metaOf(s);
                  const handle = meta.username || s.Name;
                  const col = groupByName(s.Profile || meta.profile)?.color;
                  const isSel = warmupSel.has(s.Name);
                  return (
                    <Box key={s.Name} onClick={() => warmupToggleOne(s.Name)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer',
                        bgcolor: isSel ? alpha('#f97316', 0.1) : 'transparent', border: `1px solid ${isSel ? alpha('#f97316', 0.3) : 'transparent'}`,
                        '&:hover': { bgcolor: isSel ? alpha('#f97316', 0.12) : alpha('#fff', 0.03) } }}>
                      <Checkbox size="small" checked={isSel} sx={{ p: 0, color: t.mutedFaint, '&.Mui-checked': { color: '#f97316' } }} />
                      <Avatar src={meta.profile_pic_url || undefined} sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: alpha('#f97316', 0.2), color: '#fdba74' }}>
                        {(handle || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography noWrap sx={{ flex: 1, fontSize: '0.82rem' }}>@{handle}</Typography>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: col || t.mutedFaint }} />
                    </Box>
                  );
                })}
                {warmupVisible.length === 0 && <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint, py: 2, textAlign: 'center' }}>Aucun compte</Typography>}
              </Box>
            </Box>

            {/* ===== colonne réglages ===== */}
            <Box sx={{ flex: 1, minWidth: 0, p: 2.5, maxHeight: { md: '64vh' }, overflowY: { md: 'auto' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, px: 1.5, py: 1, borderRadius: 2,
                bgcolor: alpha('#f97316', 0.08), border: `1px solid ${alpha('#f97316', 0.25)}` }}>
                <Typography sx={{ fontSize: '1.1rem' }}>🔥</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#fdba74' }}>
                  <b style={{ color: '#fed7aa' }}>{warmupCount}</b> compte{warmupCount > 1 ? 's' : ''} sélectionné{warmupCount > 1 ? 's' : ''} pour le warm-up.
                </Typography>
              </Box>

          {/* ---- rythme ---- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rythme</Typography>
            <Tooltip title="Les plages « min → max » sont tirées au hasard pour chaque compte : jamais deux comptes identiques (anti-détection).">
              <Typography sx={{ fontSize: '0.66rem', color: '#fdba74', cursor: 'help' }}>· plages aléatoires par compte</Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5, mb: 1.5 }}>
            <RangeSlider label="Durée / compte (minutes)" unit=" min" min={1} max={60}
              value={[warmupCfg.durationMin, warmupCfg.durationMax]}
              onChange={(v) => setWarmupCfg((p) => ({ ...p, durationMin: v[0], durationMax: v[1] }))} />
            <RangeSlider label="Délai entre actions" unit="s" min={2} max={60}
              value={[warmupCfg.minDelaySec, warmupCfg.maxDelaySec]}
              onChange={(v) => setWarmupCfg((p) => ({ ...p, minDelaySec: v[0], maxDelaySec: v[1] }))} />
          </Box>
          <Box sx={{ mb: 2.5, width: 'calc(50% - 10px)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography sx={{ fontSize: '0.72rem', color: t.muted }}>Comptes en parallèle</Typography>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#f97316' }}>{warmupCfg.concurrency}</Typography>
            </Box>
            <Slider size="small" value={Number(warmupCfg.concurrency) || 1} min={1} max={5} step={1} marks
              onChange={(_, v) => setCfg('concurrency', v)} sx={{ color: '#f97316', mx: 0.5, width: 'calc(100% - 8px)' }} />
          </Box>

          {/* ---- actions ---- */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Actions</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>

            {/* like */}
            <Box sx={{ borderRadius: 2.5, border: `1px solid ${warmupCfg.like ? alpha('#f472b6', 0.4) : t.border}`,
              bgcolor: warmupCfg.like ? alpha('#f472b6', 0.05) : t.input, transition: 'all .2s ease', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#f472b6', 0.15) }}>
                  <LikeIcon sx={{ fontSize: 17, color: '#f472b6' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>Liker des posts</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Like une partie des posts du feed.</Typography>
                </Box>
                <StatusSwitch checked={!!warmupCfg.like} onChange={(e) => setCfg('like', e.target.checked)} />
              </Box>
              <Collapse in={!!warmupCfg.like} unmountOnExit>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, px: 1.5, pb: 1.75 }}>
                  <PctSlider label="% de posts likés" color="#f472b6" value={warmupCfg.likeProb} onChange={(v) => setCfg('likeProb', v)} />
                  <RangeSlider label="Max likes / compte" min={1} max={40} color="#f472b6"
                    value={[warmupCfg.maxLikes, warmupCfg.maxLikesHi]}
                    onChange={(v) => setWarmupCfg((p) => ({ ...p, maxLikes: v[0], maxLikesHi: v[1] }))} />
                </Box>
              </Collapse>
            </Box>

            {/* view */}
            <Box sx={{ borderRadius: 2.5, border: `1px solid ${warmupCfg.view ? alpha('#93c5fd', 0.4) : t.border}`,
              bgcolor: warmupCfg.view ? alpha('#93c5fd', 0.05) : t.input, transition: 'all .2s ease', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#93c5fd', 0.15) }}>
                  <OpenInNewIcon sx={{ fontSize: 17, color: '#93c5fd' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>Ouvrir / regarder des posts</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Ouvre des posts comme un vrai lecteur.</Typography>
                </Box>
                <StatusSwitch checked={!!warmupCfg.view} onChange={(e) => setCfg('view', e.target.checked)} />
              </Box>
              <Collapse in={!!warmupCfg.view} unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.75, width: '50%' }}>
                  <PctSlider label="% de posts ouverts" color="#93c5fd" value={warmupCfg.viewProb} onChange={(v) => setCfg('viewProb', v)} />
                </Box>
              </Collapse>
            </Box>

            {/* comment */}
            <Box sx={{ borderRadius: 2.5, border: `1px solid ${warmupCfg.comment ? alpha('#c4b5fd', 0.4) : t.border}`,
              bgcolor: warmupCfg.comment ? alpha('#c4b5fd', 0.05) : t.input, transition: 'all .2s ease', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#c4b5fd', 0.15) }}>
                  <ReplyIcon sx={{ fontSize: 17, color: '#c4b5fd' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>Commenter</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Répond avec un de tes textes, au hasard.</Typography>
                </Box>
                <StatusSwitch checked={!!warmupCfg.comment} onChange={(e) => setCfg('comment', e.target.checked)} />
              </Box>
              <Collapse in={!!warmupCfg.comment} unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <TextField size="small" fullWidth multiline minRows={3} label="Commentaires possibles (un par ligne)"
                    placeholder={'trop bien 🔥\nj\'adore\nmdrr\n100% d\'accord'} value={warmupCfg.commentsText}
                    onChange={(e) => setCfg('commentsText', e.target.value)} sx={{ mb: 1.25 }} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <PctSlider label="% de posts commentés" color="#c4b5fd" value={warmupCfg.commentProb} onChange={(v) => setCfg('commentProb', v)} />
                    <RangeSlider label="Max commentaires / compte" min={1} max={20} color="#c4b5fd"
                      value={[warmupCfg.maxComments, warmupCfg.maxCommentsHi]}
                      onChange={(v) => setWarmupCfg((p) => ({ ...p, maxComments: v[0], maxCommentsHi: v[1] }))} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25, px: 1, py: 0.75, borderRadius: 1.5,
                    bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.2)}` }}>
                    <Typography sx={{ fontSize: '0.9rem' }}>⚠️</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#fca5a5' }}>
                      L'action la plus risquée : garde un max bas (1-3) et un % faible.
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>

          {/* ---- planification (optionnel) ---- */}
          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${t.border}` }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Planifier <span style={{ color: t.mutedFaint, fontWeight: 400, textTransform: 'none' }}>· optionnel — répéter sur plusieurs dates</span>
            </Typography>

            {/* timezone + heure */}
            <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <InputLabel>Fuseau</InputLabel>
                <Select label="Fuseau" value={warmupTz} onChange={(e) => setWarmupTz(e.target.value)}
                  renderValue={(v) => { const z = WARM_TZS.find((x) => x.tz === v); return z ? `${z.label} (${z.abbr})` : v; }}>
                  {WARM_TZS.map((z) => (
                    <MenuItem key={z.tz} value={z.tz}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                        <span><b>{z.label}</b> <span style={{ color: t.mutedFaint }}>({z.abbr})</span></span>
                        <span style={{ color: t.mutedFaint }}>{tzNow(z.tz)}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="time" label="Heure" value={warmupTime}
                onChange={(e) => setWarmupTime(e.target.value)} sx={{ width: 130 }} InputLabelProps={{ shrink: true }} />
            </Box>

            {/* calendrier */}
            <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 1.5, bgcolor: t.background }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <IconButton size="small" onClick={() => setWarmupMonth(new Date(warmupMonth.getFullYear(), warmupMonth.getMonth() - 1, 1))}>‹</IconButton>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{WARM_MONTHS[warmupMonth.getMonth()]} {warmupMonth.getFullYear()}</Typography>
                <IconButton size="small" onClick={() => setWarmupMonth(new Date(warmupMonth.getFullYear(), warmupMonth.getMonth() + 1, 1))}>›</IconButton>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {WARM_WD.map((w, i) => <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.66rem', color: t.mutedFaint, py: 0.5 }}>{w}</Typography>)}
                {warmupCalDays.map((d, i) => {
                  if (!d) return <Box key={i} />;
                  const k = wDateKey(d);
                  const isSel = warmupDates.has(k);
                  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
                  const isPast = d < t0;
                  const isToday = k === wDateKey(new Date());
                  return (
                    <Box key={i} onClick={() => !isPast && toggleWarmDate(d)}
                      sx={{ textAlign: 'center', py: 0.55, borderRadius: 1.5, fontSize: '0.78rem', cursor: isPast ? 'not-allowed' : 'pointer',
                        color: isPast ? t.mutedFaint : isSel ? '#fff' : t.foreground, opacity: isPast ? 0.35 : 1,
                        bgcolor: isSel ? '#f97316' : 'transparent', fontWeight: isSel ? 700 : 500,
                        border: isToday && !isSel ? `1px solid #f97316` : '1px solid transparent',
                        '&:hover': isPast ? {} : { bgcolor: isSel ? '#ea580c' : alpha('#fff', 0.06) } }}>
                      {d.getDate()}
                    </Box>
                  );
                })}
              </Box>
              {/* raccourcis */}
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {[['7 j', 7], ['15 j', 15], ['30 j', 30]].map(([lbl, n]) => (
                  <Box key={n} role="button" onClick={() => setWarmupDates((prev) => { const s = new Set(prev); for (let i = 0; i < n; i += 1) { const dd = new Date(); dd.setDate(dd.getDate() + i); s.add(wDateKey(dd)); } return s; })}
                    sx={{ px: 1, py: 0.35, borderRadius: 1.5, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${t.border}`, color: t.muted,
                      '&:hover': { borderColor: '#f97316', color: '#fdba74' } }}>{n} prochains j</Box>
                ))}
                {warmupDates.size > 0 && (
                  <Box role="button" onClick={() => setWarmupDates(new Set())} sx={{ px: 1, py: 0.35, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', color: t.mutedFaint, '&:hover': { color: t.foreground } }}>Effacer</Box>
                )}
              </Box>
            </Box>
            {warmupDates.size > 0 && (
              <Typography sx={{ fontSize: '0.74rem', color: '#fdba74', mt: 0.75 }}>
                {warmupDates.size} date(s) · warm-up à <b>{warmupTime}</b> heure {WARM_TZS.find((z) => z.tz === warmupTz)?.label}.
              </Typography>
            )}

            {/* warm-ups déjà programmés */}
            {warmupSchedules.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>À venir</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 140, overflowY: 'auto' }}>
                  {warmupSchedules.map((s) => (
                    <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.65, borderRadius: 1.5, bgcolor: alpha('#fff', 0.025), border: `1px solid ${t.border}` }}>
                      <ScheduleIcon sx={{ fontSize: 15, color: '#fdba74' }} />
                      <Typography sx={{ fontSize: '0.78rem', flex: 1 }}>{fmtRunAt(s.runAt, warmupTz)}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>{s.accounts} compte(s)</Typography>
                      <IconButton size="small" onClick={() => cancelWarmupSchedule(s.id)} sx={{ color: t.mutedFaint }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* ---- progression live ---- */}
          <Collapse in={!!(warmupRun.accounts && warmupRun.accounts.length > 0)} unmountOnExit>
            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {warmupRun.running ? 'En cours' : 'Dernier warm-up'}
                </Typography>
                {warmupRun.running && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fb923c', animation: 'warmPulse 1.1s ease-in-out infinite' }} />
                    <Typography sx={{ fontSize: '0.72rem', color: '#fdba74', fontWeight: 600 }}>live</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.6, pr: 0.5 }}>
                {warmupRun.accounts.map((a) => {
                  const st = WARM_STATUS[a.status] || WARM_STATUS.pending;
                  const running = a.status === 'running';
                  const events = a.events || [];
                  const open = warmupExpanded.has(a.name);
                  const hasDetail = events.length > 0;
                  return (
                    <Box key={a.name} sx={{ flexShrink: 0, borderRadius: 2, bgcolor: alpha('#fff', 0.025), borderLeft: `3px solid ${st.color}`,
                      animation: 'warmIn .25s ease both', overflow: 'hidden' }}>
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.85, overflow: 'hidden',
                        cursor: hasDetail ? 'pointer' : 'default' }}
                        onClick={() => hasDetail && toggleWarmExpand(a.name)}>
                        {running && (
                          <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: `linear-gradient(90deg, transparent, ${alpha('#f97316', 0.10)}, transparent)`,
                            backgroundSize: '200% 100%', animation: 'warmSheen 1.8s linear infinite' }} />
                        )}
                        <Box sx={{ width: 18, display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                          {hasDetail
                            ? <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: t.mutedFaint, lineHeight: 1, userSelect: 'none' }}>{open ? '–' : '+'}</Typography>
                            : null}
                        </Box>
                        {running
                          ? <CircularProgress size={13} sx={{ color: '#fdba74' }} />
                          : <Box sx={{ width: 13, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {a.status === 'done' ? <CheckIcon sx={{ fontSize: 14, color: st.color }} />
                                : a.status === 'failed' ? <CloseIcon sx={{ fontSize: 14, color: st.color }} />
                                : <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.color }} />}
                            </Box>}
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', zIndex: 1 }}>
                          @{a.username || a.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, zIndex: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#f472b6' }}>♥ {a.likes}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#c4b5fd' }}>💬 {a.comments}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#93c5fd' }}>👁 {a.views}</Typography>
                        </Box>
                        <Tooltip title={a.message || ''}>
                          <Typography sx={{ fontSize: '0.68rem', color: st.color, fontWeight: 700, width: 62, textAlign: 'right', zIndex: 1 }}>{st.label}</Typography>
                        </Tooltip>
                      </Box>
                      <Collapse in={open} unmountOnExit>
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.4, borderTop: `1px solid ${t.border}`, bgcolor: alpha('#000', 0.2) }}>
                          {[...events].reverse().map((ev, i) => {
                            const ic = ev.kind === 'like' ? '♥' : ev.kind === 'comment' ? '💬' : '👁';
                            const col = ev.kind === 'like' ? '#f472b6' : ev.kind === 'comment' ? '#c4b5fd' : '#93c5fd';
                            const verb = ev.kind === 'like' ? 'like' : ev.kind === 'comment' ? 'commentaire à' : 'ouvre';
                            const link = ev.code ? `https://www.threads.com/t/${ev.code}` : (ev.author ? `https://www.threads.com/@${ev.author}` : null);
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.72rem' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: col, width: 16 }}>{ic}</Typography>
                                <Typography sx={{ color: t.mutedFaint }}>{verb}</Typography>
                                {link
                                  ? <a href={link} target="_blank" rel="noreferrer" style={{ color: '#fdba74', textDecoration: 'none' }}>@{ev.author || 'post'}</a>
                                  : <Typography sx={{ color: t.foreground }}>@{ev.author || 'post'}</Typography>}
                                {ev.kind === 'comment' && ev.text && (
                                  <Typography sx={{ color: t.muted, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>« {ev.text} »</Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Collapse>
            </Box>{/* fin colonne réglages */}
          </Box>{/* fin 2 colonnes */}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${t.border}` }}>
          <Button onClick={() => setWarmupOpen(false)} sx={{ color: t.muted }}>Fermer</Button>
          <Box sx={{ flex: 1 }} />
          {warmupRun.running && (
            <Button variant="outlined" color="error" onClick={stopWarmupRun} startIcon={<CloseIcon sx={{ fontSize: 18 }} />}>Arrêter</Button>
          )}
          {warmupDates.size > 0 ? (
            <Button variant="contained" onClick={scheduleWarmupRun} disabled={warmupStarting || warmupCount === 0}
              startIcon={warmupStarting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ScheduleIcon />}
              sx={{ px: 2.5, background: 'linear-gradient(135deg, #fb923c, #ea580c)', color: '#fff',
                boxShadow: `0 8px 20px -8px ${alpha('#f97316', 0.8)}`,
                '&:hover': { background: 'linear-gradient(135deg, #f97316, #c2410c)' },
                '&.Mui-disabled': { background: t.input, color: t.mutedFaint, boxShadow: 'none' } }}>
              Programmer · {warmupDates.size} date(s)
            </Button>
          ) : (
            <Button variant="contained" onClick={startWarmupRun} disabled={warmupStarting || warmupRun.running || warmupCount === 0}
              startIcon={warmupStarting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AutorenewIcon />}
              sx={{ px: 2.5, background: 'linear-gradient(135deg, #fb923c, #ea580c)', color: '#fff',
                boxShadow: `0 8px 20px -8px ${alpha('#f97316', 0.8)}`,
                '&:hover': { background: 'linear-gradient(135deg, #f97316, #c2410c)' },
                '&.Mui-disabled': { background: t.input, color: t.mutedFaint, boxShadow: 'none' } }}>
              {warmupRun.running ? 'En cours…' : `Lancer${warmupCount ? ` · ${warmupCount}` : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== add group dialog ===== */}
      <Dialog open={addGroupOpen} onClose={() => setAddGroupOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Nouveau groupe</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nom du groupe" fullWidth value={newGroup.name}
            onChange={(e) => setNewGroup((p) => ({ ...p, name: e.target.value }))} placeholder="ex: Justine Lien" />
          <Typography sx={{ fontSize: '0.78rem', color: t.muted, mt: 2, mb: 1 }}>Couleur</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {GROUP_COLORS.map((c) => (
              <Box key={c} onClick={() => setNewGroup((p) => ({ ...p, color: c }))}
                sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                  border: newGroup.color === c ? `2px solid #fff` : `2px solid transparent`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {newGroup.color === c && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setAddGroupOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAddGroup}>Créer</Button>
        </DialogActions>
      </Dialog>

      {/* ===== create session dialog ===== */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Nouveau compte</DialogTitle>
        <DialogContent>
          {/* mode toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {[['simple', 'Simple'], ['bulk', 'Bulk upload'], ['autologin', 'Login auto']].map(([k, l]) => (
              <Box key={k} role="button" onClick={() => setCreateMode(k)}
                sx={{ flex: 1, textAlign: 'center', py: 0.85, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.85rem', fontWeight: createMode === k ? 600 : 500,
                  bgcolor: createMode === k ? alpha(t.primary, 0.15) : t.background, color: createMode === k ? '#c7d2fe' : t.muted,
                  border: `1px solid ${createMode === k ? alpha(t.primary, 0.4) : t.border}` }}>{l}</Box>
            ))}
          </Box>

          {createMode === 'simple' ? (<>
          <Alert severity="info" sx={{ mb: 2 }}>
            Colle le JSON des cookies (export Cookie-Editor) ou importe un <code>.json</code>. Le pseudo et les abonnés sont récupérés automatiquement à la création.
          </Alert>
          <TextField margin="dense" label="Nom de la session (optionnel — défaut: pseudo)" fullWidth value={createForm.name}
            onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))} sx={{ mb: 1 }} />
          <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
            <InputLabel>Groupe</InputLabel>
            <Select label="Groupe" value={createForm.profile} onChange={(e) => setCreateForm((p) => ({ ...p, profile: e.target.value }))}>
              <MenuItem value=""><em>Sans groupe</em></MenuItem>
              {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Cookies (JSON)" fullWidth multiline minRows={4} maxRows={10}
            value={typeof createForm.cookies === 'string' ? createForm.cookies : ''}
            onChange={(e) => setCookiesValue(e.target.value)}
            placeholder='[{"name":"sessionid","value":"..."}, ...]' InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem' } }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Button component="label" size="small" variant="outlined" color="inherit" sx={{ borderColor: t.border, color: t.muted }}>
              Importer .json
              <input type="file" accept=".json,application/json" hidden onChange={handleCookieFile} />
            </Button>
            {createForm.cookiesFileName && <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>{createForm.cookiesFileName}</Typography>}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={handleTestCookies} disabled={!createForm.cookies || testingCookies}>
              {testingCookies ? <CircularProgress size={16} /> : 'Tester la connexion'}
            </Button>
          </Box>
          {/* cookie analysis */}
          {cookieInfo.state === 'valid' && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label={`${cookieInfo.count} cookies`} sx={{ bgcolor: alpha('#fff', 0.05) }} />
              <Chip size="small" icon={cookieInfo.hasSession ? <CheckIcon sx={{ fontSize: 14 }} /> : <CloseIcon sx={{ fontSize: 14 }} />} label="sessionid"
                color={cookieInfo.hasSession ? 'success' : 'error'} variant="outlined" />
              <Chip size="small" icon={cookieInfo.hasCsrf ? <CheckIcon sx={{ fontSize: 14 }} /> : <CloseIcon sx={{ fontSize: 14 }} />} label="csrftoken"
                color={cookieInfo.hasCsrf ? 'success' : 'error'} variant="outlined" />
              {cookieInfo.dsUserId && <Chip size="small" label={`id ${cookieInfo.dsUserId}`} sx={{ bgcolor: alpha('#fff', 0.05) }} />}
              {rurRemoved && <Chip size="small" icon={<CheckIcon sx={{ fontSize: 14 }} />} label="cookie rur retiré" color="success" variant="outlined" />}
            </Box>
          )}
          {cookieInfo.state === 'invalid' && <Alert severity="warning" sx={{ mt: 1.5 }}>{cookieInfo.message}</Alert>}
          {testResult && (
            <Alert severity={testResult.valid ? 'success' : 'error'} sx={{ mt: 1.5 }}>
              {testResult.valid ? `Connecté en tant que @${testResult.username}${testResult.follower_count != null ? ` · ${testResult.follower_count} abonnés` : ''}`
                : (testResult.error || 'Session invalide')}
            </Alert>
          )}
          </>) : createMode === 'bulk' ? (<>
          {/* ===== BULK ===== */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Importe plusieurs comptes d'un coup : sélectionne plusieurs fichiers <code>.json</code> (un par compte) <b>ou</b> colle plusieurs JSON de cookies à la suite (ou un par ligne).
          </Alert>
          <FormControl fullWidth margin="dense" sx={{ mb: 1.5 }}>
            <InputLabel>Groupe (pour tous les comptes)</InputLabel>
            <Select label="Groupe (pour tous les comptes)" value={bulk.group} onChange={(e) => setBulk((p) => ({ ...p, group: e.target.value }))}>
              <MenuItem value=""><em>Sans groupe</em></MenuItem>
              {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button component="label" size="small" variant="outlined" color="inherit" sx={{ borderColor: t.border, color: t.muted }}>
              Sélectionner des .json (multiples)
              <input type="file" accept=".json,application/json" hidden multiple onChange={handleBulkFiles} />
            </Button>
            {bulk.files.length > 0 && (
              <>
                <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>{bulk.files.length} fichier(s)</Typography>
                <Button size="small" color="inherit" sx={{ color: t.mutedFaint }} onClick={() => setBulk((p) => ({ ...p, files: [] }))}>vider</Button>
              </>
            )}
          </Box>
          <TextField margin="dense" label="… ou colle les cookies (un JSON par compte)" fullWidth multiline minRows={5} maxRows={12}
            value={bulk.text} onChange={(e) => setBulk((p) => ({ ...p, text: e.target.value }))}
            placeholder={'[{"name":"sessionid","value":"..."}]\n[{"name":"sessionid","value":"..."}]'}
            InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: '0.74rem' } }} />
          <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint, mt: 0.5 }}>
            {bulk.files.length + extractJsonArrays(bulk.text).length} compte(s) détecté(s)
          </Typography>
          {bulk.importing && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress variant="determinate" value={bulk.total ? (bulk.done / bulk.total) * 100 : 0} />
              <Typography sx={{ fontSize: '0.78rem', color: t.muted, mt: 0.5 }}>{bulk.done}/{bulk.total} traité(s)…</Typography>
            </Box>
          )}
          {(bulk.ok.length > 0 || bulk.fail.length > 0) && !bulk.importing && (
            <Box sx={{ mt: 1.5 }}>
              {bulk.ok.length > 0 && <Alert severity="success" sx={{ mb: 1 }}>{bulk.ok.length} créé(s){bulk.ok.length <= 10 ? ` : ${bulk.ok.join(', ')}` : ''}</Alert>}
              {bulk.fail.length > 0 && <Alert severity="warning">{bulk.fail.length} échec(s) : {bulk.fail.slice(0, 5).join(' · ')}{bulk.fail.length > 5 ? '…' : ''}</Alert>}
            </Box>
          )}
          </>) : (<>
          {/* ===== LOGIN AUTO ===== */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Colle une ligne par compte au format <code>identifiant&nbsp;&nbsp;mot_de_passe&nbsp;&nbsp;secret_totp</code> (séparés par des espaces ou tabulations). Le serveur se connecte à chaque compte via un <b>proxy 4G roté</b> (une IP fraîche par compte) et récupère les cookies automatiquement. Le traitement tourne <b>en arrière-plan côté serveur</b> : tu peux quitter la page ou fermer l'onglet, la progression reste visible ici.
          </Alert>
          <FormControl fullWidth margin="dense" sx={{ mb: 1.5 }}>
            <InputLabel>Groupe (pour tous les comptes)</InputLabel>
            <Select label="Groupe (pour tous les comptes)" value={auto.group} onChange={(e) => setAuto((p) => ({ ...p, group: e.target.value }))}>
              <MenuItem value=""><em>Sans groupe</em></MenuItem>
              {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Comptes (une ligne par compte)" fullWidth multiline minRows={5} maxRows={14}
            value={auto.text} onChange={(e) => setAuto((p) => ({ ...p, text: e.target.value }))}
            placeholder={'mon_compte1    motdepasse1    ABCD EFGH IJKL MNOP QRST UVWX YZ23 4567\nmon_compte2    motdepasse2    2345 6789 ABCD EFGH IJKL MNOP QRST UVWX'}
            InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: '0.74rem' } }} />
          {auto.items.length === 0 && (
            <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint, mt: 0.5 }}>
              {parseAutoLines(auto.text).length} compte(s) détecté(s)
            </Typography>
          )}
          {auto.items.length > 0 && (() => {
            const done = auto.items.filter((x) => x.status === 'ok' || x.status === 'fail').length;
            const okN = auto.items.filter((x) => x.status === 'ok').length;
            const failN = auto.items.filter((x) => x.status === 'fail').length;
            return (
              <Box sx={{ mt: 2 }}>
                {/* barre de progression globale */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LinearProgress variant="determinate" value={(done / auto.items.length) * 100}
                    sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha('#fff', 0.06),
                      '& .MuiLinearProgress-bar': { borderRadius: 3, transition: 'transform .4s ease' } }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: t.muted, minWidth: 38, textAlign: 'right' }}>
                    {done}/{auto.items.length}
                  </Typography>
                </Box>
                {/* liste live par compte */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 280, overflowY: 'auto',
                  '@keyframes autoPulse': { '0%,100%': { opacity: 0.55 }, '50%': { opacity: 1 } } }}>
                  {auto.items.map((it, i) => {
                    const running = it.status === 'running';
                    const ok = it.status === 'ok';
                    const fail = it.status === 'fail';
                    const cancelled = it.status === 'cancelled' || it.status === 'interrupted';
                    const accent = ok ? t.success : fail ? t.error : running ? t.primary : t.border;
                    return (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 1.1, borderRadius: 1.5,
                        borderLeft: `2.5px solid ${running || ok || fail ? accent : 'transparent'}`,
                        bgcolor: running ? alpha(t.primary, 0.08) : (ok ? alpha(t.success, 0.06) : (fail ? alpha(t.error, 0.06) : t.background)),
                        opacity: cancelled ? 0.55 : 1,
                        transition: 'background-color .25s ease, border-color .25s ease, opacity .25s ease',
                      }}>
                        {/* icône de statut */}
                        <Box sx={{ width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {running && <CircularProgress size={14} thickness={5} />}
                          {ok && <CheckIcon sx={{ fontSize: 17, color: t.success }} />}
                          {fail && <CloseIcon sx={{ fontSize: 17, color: t.error }} />}
                          {(it.status === 'pending' || cancelled) && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: t.mutedFaint }} />}
                        </Box>
                        {/* pseudo */}
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap',
                          color: (it.status === 'pending' || cancelled) ? t.mutedFaint : t.foreground,
                          maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          @{it.username}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }} />
                        {/* statut / phase / erreur à droite */}
                        {running && (
                          <Typography sx={{ fontSize: '0.75rem', color: '#c7d2fe', whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis', animation: 'autoPulse 1.4s ease-in-out infinite' }}>
                            {it.phase}
                          </Typography>
                        )}
                        {ok && (
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: t.success, whiteSpace: 'nowrap' }}>
                            connecté{it.followers ? ` · ${it.followers} abonnés` : ''}
                          </Typography>
                        )}
                        {fail && (
                          <Tooltip title={it.error || ''}>
                            <Typography sx={{ fontSize: '0.73rem', color: '#fca5a5', whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 210 }}>
                              {it.error}
                            </Typography>
                          </Tooltip>
                        )}
                        {it.status === 'pending' && (
                          <Typography sx={{ fontSize: '0.73rem', color: t.mutedFaint, whiteSpace: 'nowrap' }}>en attente</Typography>
                        )}
                        {cancelled && (
                          <Typography sx={{ fontSize: '0.73rem', color: t.mutedFaint, whiteSpace: 'nowrap' }}>
                            {it.status === 'interrupted' ? 'interrompu' : 'annulé'}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {/* récap final */}
                {!auto.running && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    {okN > 0 && <Chip size="small" icon={<CheckIcon sx={{ fontSize: 15 }} />} label={`${okN} connecté(s)`} color="success" variant="outlined" />}
                    {failN > 0 && <Chip size="small" icon={<CloseIcon sx={{ fontSize: 15 }} />} label={`${failN} échec(s)`} color="error" variant="outlined" />}
                    {auto.items.some((x) => x.status === 'cancelled' || x.status === 'interrupted') && (
                      <Chip size="small" label={`${auto.items.filter((x) => x.status === 'cancelled' || x.status === 'interrupted').length} interrompu(s)`} variant="outlined" sx={{ color: t.mutedFaint, borderColor: t.border }} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" color="inherit" sx={{ color: t.mutedFaint }} onClick={() => autoLoginStore.clear()}>Effacer</Button>
                  </Box>
                )}
              </Box>
            );
          })()}
          </>)}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setCreateOpen(false)}>Fermer</Button>
          {createMode === 'simple' ? (
            <Button variant="contained" onClick={handleCreateSubmit} disabled={createLoading}>
              {createLoading ? <CircularProgress size={20} /> : 'Créer'}
            </Button>
          ) : createMode === 'bulk' ? (
            <Button variant="contained" onClick={handleBulkImport} disabled={bulk.importing}>
              {bulk.importing ? `Import… ${bulk.done}/${bulk.total}` : `Importer ${bulk.files.length + extractJsonArrays(bulk.text).length || ''} compte(s)`}
            </Button>
          ) : auto.running ? (
            <Button variant="contained" color="error" onClick={cancelAutoLogin}>
              Annuler ({auto.items.filter((x) => x.status === 'ok' || x.status === 'fail').length}/{auto.items.length})
            </Button>
          ) : (
            <Button variant="contained" onClick={handleAutoLogin}>
              {`Connecter ${parseAutoLines(auto.text).length || ''} compte(s)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== edit dialog ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Éditer le compte</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Pseudo Threads (username)" fullWidth value={editForm.username}
            onChange={(e) => setEditForm((p) => ({ ...p, username: e.target.value }))} sx={{ mb: 1 }} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Groupe</InputLabel>
            <Select label="Groupe" value={editForm.profile} onChange={(e) => setEditForm((p) => ({ ...p, profile: e.target.value }))}>
              <MenuItem value=""><em>Sans groupe</em></MenuItem>
              {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="caption" sx={{ display: 'block', mt: 2.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: t.mutedFaint, fontWeight: 600 }}>
            Persona — variables des posts
          </Typography>
          <TextField margin="dense" label="Nom affiché ({name} · {p_firstname} = 1er mot)" fullWidth value={editForm.full_name}
            onChange={(e) => setEditForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="ex: Laura Mallet" sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField margin="dense" label="Âge ({p_age})" type="number" value={editForm.p_age}
              onChange={(e) => setEditForm((p) => ({ ...p, p_age: e.target.value }))} sx={{ width: 130 }} />
            <TextField margin="dense" label="Origine ({p_origin})" fullWidth value={editForm.p_origin}
              onChange={(e) => setEditForm((p) => ({ ...p, p_origin: e.target.value }))} placeholder="ex: française" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField margin="dense" label="Ville ({p_location})" fullWidth value={editForm.p_location}
              onChange={(e) => setEditForm((p) => ({ ...p, p_location: e.target.value }))} placeholder="ex: Paris" />
            <TextField margin="dense" label="Métier ({p_occupation})" fullWidth value={editForm.p_occupation}
              onChange={(e) => setEditForm((p) => ({ ...p, p_occupation: e.target.value }))} placeholder="ex: étudiante" />
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 2.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: t.mutedFaint, fontWeight: 600 }}>
            Proxy
          </Typography>
          <TextField margin="dense" label="Proxy URL" fullWidth value={editForm.proxy_url}
            onChange={(e) => setEditForm((p) => ({ ...p, proxy_url: e.target.value }))}
            placeholder="direct (aucun proxy)"
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SignalIcon sx={{ fontSize: 16, color: editForm.proxy_url && editForm.proxy_url.toLowerCase() !== 'direct' ? t.success : t.mutedFaint }} /></InputAdornment>
            ) }} />
          {/* Bloc 4G : lien de rotation d'IP (optionnel), même design que la page Proxies. */}
          <Box sx={{ mt: 1.5, p: 1.75, borderRadius: 2, bgcolor: alpha(t.primary, 0.05), border: `1px solid ${alpha(t.primary, editRotate ? 0.4 : 0.2)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <SignalIcon sx={{ fontSize: 16, color: t.primary }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Proxy 4G — IP tournante</Typography>
              {editRotate ? (
                <Chip label="4G" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${t.primary}22`, color: '#c7d2fe', border: `1px solid ${t.primary}55` }} />
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>optionnel</Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.74rem', color: t.muted, mb: 1.25 }}>
              Après chaque post via ce proxy, l'IP est tournée (délai de sécurité) avant le compte suivant.
            </Typography>
            <TextField size="small" fullWidth label="Lien de rotation d'IP" value={editRotate}
              onChange={(e) => setEditRotate(e.target.value)} placeholder="https://.../rotate?token=..."
              disabled={!editForm.proxy_url || editForm.proxy_url.toLowerCase() === 'direct'} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
              <Button size="small" variant="outlined"
                startIcon={editRotateTesting ? <CircularProgress size={14} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
                onClick={testEditRotate} disabled={editRotateTesting || !editRotate.trim()}>Tester la rotation</Button>
            </Box>
            {editRotateResult && (
              <Alert severity={editRotateResult.success ? 'success' : 'error'} sx={{ mt: 1.25, '& .MuiAlert-message': { overflow: 'hidden' } }}>
                {editRotateResult.error ? (
                  <>Échec : {editRotateResult.error}</>
                ) : (
                  <>
                    <b>HTTP {editRotateResult.status}</b> — {editRotateResult.success ? 'la rotation répond ✅' : 'réponse en erreur ⚠️'}
                    {editRotateResult.body ? (
                      <Box component="pre" sx={{ mt: 0.75, mb: 0, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.3)', fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 110, overflow: 'auto' }}>{editRotateResult.body}</Box>
                    ) : null}
                  </>
                )}
              </Alert>
            )}
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 2.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: t.mutedFaint, fontWeight: 600 }}>
            Technique
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>User-Agent</InputLabel>
            <Select label="User-Agent" value={editForm.user_agent} onChange={(e) => setEditForm((p) => ({ ...p, user_agent: e.target.value }))}>
              {editForm.user_agent && !UA_PRESETS.some(([, ua]) => ua === editForm.user_agent) && (
                <MenuItem value={editForm.user_agent} sx={{ fontStyle: 'italic', color: 'orange' }}>Actuel (personnalisé)</MenuItem>
              )}
              {UA_PRESETS.map(([label, ua]) => <MenuItem key={ua} value={ua}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Cooldown (minutes)" type="number" fullWidth value={editForm.cooldown_min}
            onChange={(e) => setEditForm((p) => ({ ...p, cooldown_min: parseInt(e.target.value) || 0 }))} />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setEditOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleEditSubmit}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ===== delete dialog ===== */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Supprimer le compte</DialogTitle>
        <DialogContent>
          <Typography>Supprimer définitivement <b>{deleteTarget?.Name}</b> ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== bulk delete ===== */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Suppression multiple</DialogTitle>
        <DialogContent><Typography>Supprimer <b>{selection.size}</b> compte(s) ?</Typography></DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setBulkDeleteOpen(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleBulkDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== followers history ===== */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Historique des abonnés · {historyData.session?.Name}</DialogTitle>
        <DialogContent>
          {historyData.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : historyData.history.length === 0 ? (
            <Typography sx={{ color: t.muted, py: 2 }}>Aucun historique disponible.</Typography>
          ) : (
            <Box sx={{ height: 280, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyData.history.map((h) => {
                  // timestamp peut être une chaîne ISO/RFC3339 ou un nombre unix (s ou ms)
                  let raw = h.date ?? h.timestamp;
                  if (typeof raw === 'string' && /^\d+$/.test(raw)) raw = parseInt(raw, 10);
                  if (typeof raw === 'number') raw = raw < 1e12 ? raw * 1000 : raw;
                  const d = new Date(raw);
                  return {
                    // date + heure : plusieurs relevés le même jour restent des points
                    // distincts (sinon ils se superposent et on ne voit qu'un point).
                    date: isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
                    followers: h.followers ?? h.count,
                  };
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
                  <XAxis dataKey="date" stroke={t.mutedFaint} fontSize={11} />
                  <YAxis stroke={t.mutedFaint} fontSize={11} />
                  <RechartsTooltip contentStyle={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="followers" stroke={t.primary} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setHistoryOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* ===== stats des posts ===== */}
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <DialogTitle>Stats des derniers posts · {statsData.session?.Name}</DialogTitle>
        <DialogContent>
          {statsData.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : statsData.posts.length === 0 ? (
            <Typography sx={{ color: t.muted, py: 2 }}>Aucun post trouvé (ou compte sans publication récente).</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5 }}>
              {statsData.posts.map((p, i) => {
                const d = p.takenAt ? new Date(p.takenAt * 1000) : null;
                const dateStr = d && !isNaN(d.getTime()) ? d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                const username = statsData.session ? metaOf(statsData.session).username : null;
                const url = p.code ? `https://www.threads.com/@${username || ''}/post/${p.code}` : (username ? `https://www.threads.com/@${username}` : null);
                const Stat = ({ icon, value, color }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: t.muted }}>
                    {React.cloneElement(icon, { sx: { fontSize: 15, color: color || t.mutedFaint } })}
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: t.foreground }}>{value ?? 0}</Typography>
                  </Box>
                );
                return (
                  <Box key={i} sx={{ border: `1px solid ${t.border}`, borderRadius: 2, bgcolor: t.background, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: '0.84rem', color: t.foreground, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                        {p.text || <span style={{ color: t.mutedFaint }}>— (post image / sans texte)</span>}
                      </Typography>
                      {url && (
                        <Tooltip title="Ouvrir le post">
                          <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ color: t.mutedFaint, p: 0.3 }}>
                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Stat icon={<LikeIcon />} value={p.likes} color="#f43f5e" />
                      <Stat icon={<ReplyIcon />} value={p.replies} color={t.info} />
                      <Stat icon={<RepostIcon />} value={p.reposts} color={t.success} />
                      <Stat icon={<QuoteIcon />} value={p.quotes} color={t.warning} />
                      <Box sx={{ flex: 1 }} />
                      {dateStr && <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>{dateStr}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => statsData.session && handleViewStats(statsData.session)} disabled={statsData.loading}>Rafraîchir</Button>
          <Button color="inherit" sx={{ color: t.muted }} onClick={() => setStatsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
